import moment from 'moment'
import { ATTENDANCE_PERMISSIONS } from '@/utils/attendance-permissions'
import {
  countApprovedWfhToday,
  isApprovedLeaveStatus,
  isPresentOrHalfDayLeaveType,
  isWFHLeaveRecord,
  isWFHLeaveType,
  leaveCoversDay,
  parseAttendanceLeaveDay,
  requiresRegularizationTime,
  resolveLeaveEmpId,
  resolveMemberEmpId,
} from '@/utils/attendance-leave-types'
import { extractShiftFieldsFromMember } from '@/utils/attendance-shift-thresholds'
import {
  computeProbationTiming,
  filterActiveProbationRows,
  isOpenProbationStatus,
  isProbationStatusCompleted,
  resolveProbationStatus,
} from '@/utils/probation-timing'

export default {
  computed: {
    // ============================================
    // NEW SIMPLIFIED COMPUTED PROPERTIES
    // These directly access pre-calculated data from backend APIs
    // ============================================
    
    /**
     * KPI Stats from dashboard API (My View)
     * Returns: { leave_balance, leaves_taken, approval_pending, regularization_count, wfh_count }
     * Falls back to locally-derived counts when the API omits approval_pending / regularization_count.
     */
    kpiStats() {
      const apiStats = (this.dashboardData && this.dashboardData.kpi_stats) || {}
      const selectedEmpId = this.selectedTeamMember || this.selectedTeamWiseEmployee
      const targetEmpId = String(selectedEmpId || this.emp_id || '')
      const isFilteredEmployeeView = (this.viewMode === 'team' || this.viewMode === 'org') && selectedEmpId

      // Derive approval_pending from myLeaves when the API doesn't supply it.
      const derivedApprovalPending = (() => {
        return (this.myLeaves || []).filter(l => {
          // Personal dashboard leaves often omit emp_id — only filter when present
          if (targetEmpId && l.emp_id && String(l.emp_id) !== targetEmpId) return false
          const s = (l.status || '').trim().toLowerCase()
          return s !== '' &&
                 s !== 'approved' &&
                 s !== 'cancelled' &&
                 !s.includes('reject') &&
                 !s.includes('cancel')
        }).length
      })()

      // Derive regularization_count from regularizationCases when the API doesn't supply it.
      // Exclude today's local calendar day (day may still be in progress).
      const todayStr = moment().format('YYYY-MM-DD')
      const derivedRegularizationCount = (() => {
        return (this.regularizationCases || []).filter(r => {
          if (!r) return false
          if (targetEmpId && r.emp_id && String(r.emp_id) !== targetEmpId) return false
          const caseDate = r.datestring || r.date
          if (caseDate && moment(caseDate).format('YYYY-MM-DD') === todayStr) return false
          return true
        }).length
      })()

      // Filtered team/org employee view: derive KPIs from loaded employee data
      if (isFilteredEmployeeView) {
        const breakdown = typeof this.getDisplayLeaveBreakdown === 'function'
          ? this.getDisplayLeaveBreakdown(selectedEmpId)
          : (this.availableLeaveBreakdown || [])
        const derivedLeaveBalance = breakdown.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0)
        const derivedLeavesTaken = typeof this.getLeavesAvailedForEmployee === 'function'
          ? this.getLeavesAvailedForEmployee(selectedEmpId)
          : Math.max(0, (parseFloat(apiStats.leaves_taken) || 0) - (parseFloat(apiStats.wfh_count) || 0))

        return {
          leave_balance: derivedLeaveBalance || apiStats.leave_balance || 0,
          leaves_taken: derivedLeavesTaken,
          approval_pending: derivedApprovalPending,
          regularization_count: derivedRegularizationCount,
          wfh_count: apiStats.wfh_count ?? 0,
        }
      }

      const breakdown = typeof this.getDisplayLeaveBreakdown === 'function'
        ? this.getDisplayLeaveBreakdown(this.emp_id)
        : (this.availableLeaveBreakdown || [])
      const breakdownSum = breakdown.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0)
      const derivedLeavesTaken = typeof this.getLeavesAvailedForEmployee === 'function'
        ? this.getLeavesAvailedForEmployee(this.emp_id)
        : Object.values(this.leaveUsageByType || {}).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
      const baseBalance = breakdownSum > 0 ? breakdownSum : (apiStats.leave_balance ?? 0)
      const adjustedBalance = baseBalance
      const hasMyLeaves = Array.isArray(this.myLeaves) && this.myLeaves.length > 0
      const leavesTaken = hasMyLeaves || derivedLeavesTaken > 0
        ? derivedLeavesTaken
        : Math.max(0, (parseFloat(apiStats.leaves_taken) || 0) - (parseFloat(apiStats.wfh_count) || 0))

      // Prefer client-derived pending count when myLeaves are loaded (API often returns 0)
      const approvalPending = hasMyLeaves
        ? derivedApprovalPending
        : Math.max(Number(apiStats.approval_pending) || 0, derivedApprovalPending)

      // Prefer client-derived count (excludes today) when regularizationCases are loaded
      const hasClientRegCases = Array.isArray(this.regularizationCases)
      const result = {
        leave_balance: adjustedBalance,
        leaves_taken: leavesTaken,
        approval_pending: approvalPending,
        regularization_count: hasClientRegCases
          ? derivedRegularizationCount
          : (apiStats.regularization_count ?? derivedRegularizationCount),
        wfh_count: apiStats.wfh_count ?? 0,
      }
      return result
    },
    
    /**
     * Leave balance breakdown from dashboard API
     * Returns: [{ type, label, assigned, accrued, used, available }]
     */
    leaveBalanceBreakdownFromAPI() {
      return this.dashboardData?.leave_balance_breakdown || []
    },
    
    /**
     * Calendar days from dashboard API (pre-built with status)
     * Returns: [{ date, day, status, leave_type, in_time, out_time, is_holiday, etc. }]
     */
    calendarDays() {
      return this.dashboardData?.calendar_data || []
    },
    
    /**
     * Employee IDs for the selected account manager's team (org/team filter).
     * Returns null when no account manager is selected or team list is unavailable.
     */
    accountManagerTeamMemberIds() {
      if (!this.selectedAccountManager || !this.canApplyOnBehalfAll) return null
      if (!this.teamWiseEmployees || this.teamWiseEmployees.length === 0) return null
      return this.teamWiseEmployees.map(emp => resolveMemberEmpId(emp)).filter(id => id)
    },

    /**
     * True when org/team view shows an account manager's full team (no specific employee).
     */
    isShowingAccountManagerTeam() {
      return (this.viewMode === 'team' || this.viewMode === 'org') &&
        this.canApplyOnBehalfAll &&
        this.selectedAccountManager &&
        !this.selectedTeamWiseEmployee &&
        !this.selectedTeamMember &&
        this.accountManagerTeamMemberIds &&
        this.accountManagerTeamMemberIds.length > 0
    },

    /**
     * Team Leader aggregate surface (not a single filtered employee).
     * Used to show Pending Approvals + Today's attendance + Holidays/Announcements.
     */
    isTeamLeaderAggregateView() {
      return this.viewMode === 'team' && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
    },

    /**
     * Org aggregate surface (no single employee filter) — Team-like mid-row.
     */
    isOrgLeaderAggregateView() {
      return this.viewMode === 'org' && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
    },

    /** Team or Org aggregate mid-row (snapshot | today | leave balance). */
    isManagerAggregateView() {
      return this.isTeamLeaderAggregateView || this.isOrgLeaderAggregateView
    },

    /**
     * Leave conflicts for panels/downloads.
     * Prefer non-empty API alerts; otherwise use client-computed upcomingLeaveConflicts.
     * Note: alerts.leave_conflicts is always an array (often []), so `api || client` never
     * falls back — empty arrays are truthy in JS.
     */
    effectiveLeaveConflicts() {
      const fromApi = this.alerts && this.alerts.leave_conflicts
      if (Array.isArray(fromApi) && fromApi.length > 0) return fromApi
      return this.upcomingLeaveConflicts || []
    },

    /** Show bottom conflicts/probation card only when it has rows (avoid sparse empty tabs). */
    hasTeamConflictsOrProbationAlerts() {
      const conflicts = this.effectiveLeaveConflicts || []
      const probation = this.effectiveProbationEnding || []
      return (Array.isArray(conflicts) && conflicts.length > 0)
        || (Array.isArray(probation) && probation.length > 0)
    },

    /**
     * Team summary from team dashboard API
     * Returns: { total_members, pending_approvals, upcoming_leaves, regularization_cases }
     * pending_approvals always uses the client count aligned with Approve/Reject visibility
     * (API team_summary.pending_approvals is often 0 / HR-stage-only and must not win).
     */
    teamSummary() {
      const apiSummary = this.teamDashboardData?.team_summary || this.teamDashboardData?.org_summary
      const pendingApprovals = typeof this.approvalPendingCount === 'number' ? this.approvalPendingCount : 0

      if (this.isShowingAccountManagerTeam) {
        return {
          total_members: this.accountManagerTeamMemberIds.length,
          pending_approvals: pendingApprovals,
          upcoming_leaves: this.upcomingTeamLeaves.length,
          regularization_cases: this.teamRegularizationCount,
        }
      }

      // Prefer client count (excludes today) when regularizationCases are loaded
      const clientRegCount = this.teamRegularizationCount
      const hasClientRegCases = Array.isArray(this.regularizationCases)
      return {
        total_members: (apiSummary && apiSummary.total_members) || 0,
        pending_approvals: pendingApprovals,
        upcoming_leaves: (apiSummary && apiSummary.upcoming_leaves) || 0,
        regularization_cases: hasClientRegCases
          ? clientRegCount
          : ((apiSummary && apiSummary.regularization_cases) || 0),
      }
    },
    
    /**
     * Check if user has compensatory off balance
     */
    hasCompOffBalance() {
      const coBalance = this.coBalance || 0
      return coBalance > 0
    },
    
    /**
     * Get leave balance for the selected employee in leave detail modal
     */
    selectedEmployeeLeaveBalance() {
      if (!this.selectedLeaveForDetail || !this.selectedLeaveForDetail.emp_id) {
        return null
      }
      
      // If viewing team or org and have team leave balances data
      if ((this.viewMode === 'team' || this.viewMode === 'org') && this.teamDashboardData && this.teamDashboardData.team_leave_balances) {
        const perMember = this.teamDashboardData.team_leave_balances.per_member || []
        const memberData = perMember.find(m => m.emp_id === this.selectedLeaveForDetail.emp_id)
        
        if (memberData) {
          return `${memberData.total_available || 0} days available`
        }
      }
      
      return null
    },

    /**
     * Leave balance and breakdown for employee in Approve Leave modal
     */
    approveLeaveEmployeeBalance() {
      if (!this.selectedLeaveForApproval || !this.selectedLeaveForApproval.emp_id) return null
      if (!this.teamDashboardData || !this.teamDashboardData.team_leave_balances) return null
      const perMember = this.teamDashboardData.team_leave_balances.per_member || []
      const m = perMember.find(mem => mem.emp_id === this.selectedLeaveForApproval.emp_id)
      if (!m) return null
      const rawBreakdown = (m.breakdown || []).filter(item => item.type !== 'quarterly_wfm' && item.type !== 'wfm' && item.type !== 'wfh')
      const summaryRows = rawBreakdown
        .map(item => ({
          label: item.label || item.type || '-',
          available: parseFloat(item.available || item.value || 0) || 0,
          used: parseFloat(item.used || item.Used || item.used_days || 0) || 0,
        }))
        .filter(item => item.available > 0 || item.used > 0)
      return {
        leaveBalance: m.total_available || m.leave_balance || 0,
        leavesApplied: m.total_used || m.leaves_taken || 0,
        summaryRows,
      }
    },
    
    /**
     * Get CO balance from employee config or dashboard API
     * Calculates: allocated - used from leave_balance_breakdown
     */
    coBalance() {
      try {
        const breakdown = this.availableLeaveBreakdown || []
        const coItem = breakdown.find(item => 
          item.type === 'compensatory_off' || 
          item.type === 'comp_off' ||
          item.type === 'CO' ||
          (item.label && item.label.toLowerCase().includes('compensatory'))
        )
        
        if (coItem) {
          return coItem.available || coItem.value || 0
        }
        
        const empConfig = this.employeeConfig
        if (empConfig && empConfig.leave_balance_breakdown) {
          let configBreakdown = typeof empConfig.leave_balance_breakdown === 'string'
            ? JSON.parse(empConfig.leave_balance_breakdown)
            : empConfig.leave_balance_breakdown
          
          const allocated = parseInt(configBreakdown?.allocated?.compensatory_off) || 0
          const used = parseInt(configBreakdown?.used?.compensatory_off) || 0
          return allocated - used
        }
        
        return 0
      } catch (err) {
        console.error('Error calculating CO balance:', err)
        return 0
      }
    },
    
    /**
     * Get CO balance for display in Apply Comp Off modal
     * Returns the selected employee's CO balance if applying on behalf, otherwise returns current user's balance
     */
    displayedCoBalance() {
      try {
        // If applying on behalf of someone in team view or org view
        if (this.compOffForm.selected_employee && (this.viewMode === 'team' || this.viewMode === 'org') && this.teamDashboardData && this.teamDashboardData.team_leave_balances) {
          const perMember = this.teamDashboardData.team_leave_balances.per_member || []
          const memberData = perMember.find(m => m.emp_id === this.compOffForm.selected_employee)
          
          if (memberData && memberData.breakdown) {
            // Find CO in the breakdown
            const coItem = memberData.breakdown.find(item => 
              item.type === 'compensatory_off' || 
              item.type === 'comp_off' ||
              item.type === 'CO' ||
              (item.label && item.label.toLowerCase().includes('compensatory'))
            )
            
            if (coItem) {
              return coItem.available || 0
            }
          }
          
          // If no CO found in breakdown, return 0
          return 0
        }
        
        // Otherwise, return logged-in user's CO balance
        return this.coBalance
      } catch (err) {
        console.error('Error calculating displayed CO balance:', err)
        return 0
      }
    },
    
    /**
     * Get employee name for display in CO balance section
     */
    displayedCoBalanceEmployeeName() {
      if (this.compOffForm.selected_employee && (this.viewMode === 'team' || this.viewMode === 'org')) {
        // Find employee name from options
        const options = this.getEmployeeOptionsForCompOff
        const selectedOption = options.find(opt => opt.value === this.compOffForm.selected_employee)
        return selectedOption ? selectedOption.text : 'Selected Employee'
      }
      return 'Your'
    },
    
    /**
     * Alerts from team dashboard API (normalized field names).
     * Returns: { low_leave_balance[], leave_conflicts[], frequent_regularization[], probation_ending[] }
     */
    alerts() {
      const raw = this.teamDashboardData?.alerts || {}
      const pickArray = (...keys) => {
        for (let i = 0; i < keys.length; i += 1) {
          const val = raw[keys[i]]
          if (Array.isArray(val)) return val
        }
        return []
      }
      return {
        low_leave_balance: pickArray('low_leave_balance', 'lowLeaveBalance', 'low_leave_balances'),
        leave_conflicts: pickArray('leave_conflicts', 'leaveConflicts', 'conflicts'),
        frequent_regularization: pickArray('frequent_regularization', 'frequentRegularization'),
        probation_ending: pickArray(
          'probation_ending',
          'probationEnding',
          'probation_ending_soon',
          'probationEndingSoon',
          'probation_endings'
        ).map(item => {
          if (!item || typeof item !== 'object') return item
          const timing = computeProbationTiming(item)
          const endDate = timing.probationEndDate
            || item.probationEndDate
            || item.probation_end_date
            || item.end_date
            || item.endDate
          const daysRemaining = timing.daysRemaining != null
            ? timing.daysRemaining
            : (item.daysRemaining != null ? item.daysRemaining : item.days_remaining)
          return {
            ...item,
            emp_id: item.emp_id || item.employee_id || item.empId || item.id,
            probationEndDate: endDate,
            probation_end_date: endDate,
            daysRemaining,
            days_remaining: daysRemaining,
          }
        }),
      }
    },

    /**
     * Emp IDs whose user_probation status is Accepted (or other completion aliases).
     * Used to strip completed people from API alert fallbacks.
     */
    completedProbationEmpIdSet() {
      const set = new Set()
      ;(this.probationData || []).forEach((row) => {
        if (!row || !isProbationStatusCompleted(row)) return
        const empId = row.emp_id || row.employee_id
        if (empId != null && empId !== '') set.add(String(empId))
      })
      return set
    },

    /**
     * People currently on probation — derived from loadProbationData (user list / probation API).
     * Allowlist: user_probation status null/empty/missing OR Extended (case-insensitive).
     * Excludes Accepted, Rejected, Completed, Inactive, Closed, Active, Pending, etc.
     */
    peopleOnProbation() {
      if (this.viewMode !== 'team' && this.viewMode !== 'org') return []
      if (!this.probationData || this.probationData.length === 0) return []

      const filterByEmpId = this.selectedTeamMember || this.selectedTeamWiseEmployee
      const today = moment().startOf('day')
      const probationEmployees = []

      this.probationData.forEach(probation => {
        const empId = probation.emp_id || probation.employee_id
        if (!empId) return
        if (filterByEmpId && String(empId) !== String(filterByEmpId)) return

        // Prefer probation_status (user_probation); only Null or Extended count
        const status = resolveProbationStatus(probation)
        if (!isOpenProbationStatus(status)) return

        const timing = computeProbationTiming(probation, today)
        const statusLabel = probation.probation_status != null
          ? probation.probation_status
          : (probation.status != null ? probation.status : null)
        if (timing.probationEndDate || timing.daysRemaining != null) {
          probationEmployees.push({
            emp_id: empId,
            probation_status: statusLabel,
            status: statusLabel,
            probationEndDate: timing.probationEndDate,
            probation_end_date: timing.probationEndDate,
            daysRemaining: timing.daysRemaining,
            days_remaining: timing.daysRemaining,
          })
          return
        }

        // Null/Extended with no usable date — still currently on probation
        probationEmployees.push({
          emp_id: empId,
          probation_status: statusLabel,
          status: statusLabel,
          probationEndDate: null,
          probation_end_date: null,
          daysRemaining: null,
          days_remaining: null,
        })
      })

      return probationEmployees.sort((a, b) => {
        const da = a.daysRemaining != null ? a.daysRemaining : Number.POSITIVE_INFINITY
        const db = b.daysRemaining != null ? b.daysRemaining : Number.POSITIVE_INFINITY
        return da - db
      })
    },

    /**
     * Probation ending soon (≤30 days) — subset of peopleOnProbation for alerts tables.
     */
    probationEndingSoon() {
      const windowEnd = 30
      return (this.peopleOnProbation || []).filter(item => {
        const days = item.daysRemaining != null ? item.daysRemaining : item.days_remaining
        return days != null && days >= 0 && days <= windowEnd
      })
    },

    /**
     * Team snapshot Needs attention — People on Probation list/count.
     * Prefer client-derived peopleOnProbation once loadProbationData has rows.
     * When HR rows exist but none are Null/Extended, return [] (do not fall back to API
     * ending-soon, which can reintroduce Accepted people).
     */
    effectivePeopleOnProbation() {
      const completedIds = this.completedProbationEmpIdSet
      const onlyOpen = (rows) => filterActiveProbationRows(rows, completedIds)

      if (Array.isArray(this.probationData) && this.probationData.length > 0) {
        return onlyOpen(this.peopleOnProbation || [])
      }
      // Before HR rows load (or when sparse), fall back to ending-soon API / client subset
      const fromApi = (this.alerts && this.alerts.probation_ending) || []
      if (Array.isArray(fromApi) && fromApi.length > 0) return onlyOpen(fromApi)
      return onlyOpen(this.peopleOnProbation || [])
    },

    /**
     * Probation ending soon for tables / downloads — prefer HR-derived rows once loaded
     * (API probation_ending can reintroduce Accepted people as Overdue).
     * Same Null/Extended allowlist as People on Probation.
     */
    effectiveProbationEnding() {
      const completedIds = this.completedProbationEmpIdSet
      const onlyOpen = (rows) => filterActiveProbationRows(rows, completedIds)

      if (Array.isArray(this.probationData) && this.probationData.length > 0) {
        return onlyOpen(this.probationEndingSoon || [])
      }
      const fromApi = (this.alerts && this.alerts.probation_ending) || []
      if (Array.isArray(fromApi) && fromApi.length > 0) return onlyOpen(fromApi)
      return onlyOpen(this.probationEndingSoon || [])
    },

    /**
     * Today's attendance roster with Team snapshot employee filter applied.
     */
    filteredTeamTodayAttendanceRoster() {
      const base = this.teamTodayAttendanceRoster || {
        present: [], absent: [], wfh: [], onLeaveCount: 0, onLeaveEmpIds: [], isNonWorkingDay: false, nonWorkingLabel: 'Non-working day',
      }
      const filter = this.teamPunchRosterFilter
      if (!filter || !filter.active || !filter.empId) return base

      const matchRow = (row) => row && String(row.empId) === String(filter.empId)

      return {
        ...base,
        present: (base.present || []).filter(matchRow),
        absent: (base.absent || []).filter(matchRow),
        wfh: (base.wfh || []).filter(matchRow),
      }
    },
    
    /**
     * Pending leave approvals from team dashboard API
     */
    pendingLeaveApprovalsFromAPI() {
      return this.teamDashboardData?.pending_leave_approvals || []
    },

    /**
     * All leaves relevant to the current view mode.
     * - my view  → own leaves (myLeaves)
     * - team/org → all team leaves (allTeamLeaves)
     */
    leavesData() {
      if (this.viewMode === 'team' || this.viewMode === 'org') {
        return this.allTeamLeaves || []
      }
      return this.myLeaves || []
    },

    /**
     * Regularization cases for the current view.
     */
    regularizationData() {
      return this.regularizationCases || []
    },

    /**
     * Unified regularization data combining all sources; used for the
     * download-button visibility check in LeavesRegularizationTabs.
     */
    unifiedRegularizationData() {
      if (this.viewMode === 'team' || this.viewMode === 'org') {
        return this.teamDashboardData?.regularization_cases || this.regularizationCases || []
      }
      return this.regularizationCases || []
    },

    /**
     * Leaves sliced to the current display limit for LeavesRegularizationTabs.
     */
    displayedLeavesData() {
      return (this.leavesData || []).slice(0, this.displayedLeavesCount)
    },

    /**
     * Regularization cases sliced to the current display limit.
     */
    displayedRegularizationData() {
      return (this.regularizationData || []).slice(0, this.displayedRegularizationCount)
    },

    /**
     * Team leave balance table for Team/Org view - merge per_member with pending approval counts
     */
    teamLeaveBalanceTable() {
      if ((this.viewMode !== 'team' && this.viewMode !== 'org') || !this.teamDashboardData?.team_leave_balances?.per_member) {
        return []
      }
      let perMember = this.teamDashboardData.team_leave_balances.per_member || []
      const amTeamIds = this.accountManagerTeamMemberIds
      if (amTeamIds && amTeamIds.length > 0) {
        perMember = perMember.filter(m => m.emp_id && amTeamIds.includes(m.emp_id))
      }

      // Pending leave request counts per employee (from already-loaded allTeamLeaves)
      const pendingByEmp = Object.create(null)
      const leaves = Array.isArray(this.allTeamLeaves) ? this.allTeamLeaves.flat().filter(Boolean) : []
      leaves.forEach(leave => {
        if (!leave || leave.emp_id == null) return
        const status = String(leave.status || '').trim().toLowerCase()
        if (!status || status === 'approved' || status === 'rejected' || status === 'cancelled') return
        if (status.includes('reject') || status.includes('cancel')) return
        const isPending = status.includes('pending') || status.includes('manager approved - pending')
        if (!isPending) return
        const id = String(leave.emp_id)
        pendingByEmp[id] = (pendingByEmp[id] || 0) + 1
      })

      const pickTypeRemaining = (breakdown, matchers) => {
        if (!Array.isArray(breakdown) || breakdown.length === 0) return null
        const item = breakdown.find(b => {
          const type = String(b.type || '').toLowerCase()
          const label = String(b.label || '').toLowerCase()
          return matchers.some(m => type.includes(m) || label.includes(m))
        })
        if (!item) return null
        const n = parseFloat(item.available != null ? item.available : item.value)
        return Number.isNaN(n) ? null : n
      }

      return perMember.map(m => {
        const rawBreakdown = (m.breakdown || []).filter(item => item.type !== 'quarterly_wfm' && item.type !== 'wfm' && item.type !== 'wfh')
        const breakdown = rawBreakdown.map(item => {
          const used = parseFloat(item.used || item.Used || item.used_days || 0) || 0
          const available = parseFloat(item.available || item.value || 0) || 0
          return {
            label: item.label || item.type || '-',
            type: item.type,
            used,
            available,
          }
        })
        return {
          emp_id: m.emp_id,
          leaveBalance: m.total_available || m.leave_balance || 0,
          leavesApplied: m.total_used || m.leaves_taken || 0,
          breakdown,
          cl: pickTypeRemaining(breakdown, ['casual']),
          sl: pickTypeRemaining(breakdown, ['sick', 'medical']),
          pl: pickTypeRemaining(breakdown, ['privilege', 'pto']),
          pendingCount: pendingByEmp[String(m.emp_id)] || 0,
        }
      })
    },
    
    /**
     * My leaves from dashboard API
     */
    myLeavesFromAPI() {
      return this.dashboardData?.leaves || this.myLeaves || []
    },
    
    /**
     * Regularization cases from dashboard API
     */
    regularizationCasesFromAPI() {
      return this.dashboardData?.regularization_cases || this.regularizationCases || []
    },
    
    /**
     * Employee config from dashboard API
     */
    configFromAPI() {
      return this.dashboardData?.config || this.attendanceConfig || {}
    },
    
    /**
     * Holidays from dashboard API
     */
    holidaysFromAPI() {
      return this.dashboardData?.holidays || this.holidays || []
    },
    
    // ============================================
    // END NEW SIMPLIFIED COMPUTED PROPERTIES
    // ============================================
    
    hasActiveFilters() {
      // Check if any filters are active (Account Manager only in org view)
      return !!(
        this.dateRangeFilter ||
        (this.viewMode === 'org' && this.canApplyOnBehalfAll && this.selectedAccountManager) ||
        ((this.viewMode === 'team' || this.viewMode === 'org') && this.canApplyOnBehalf && this.selectedTeamWiseEmployee) ||
        ((this.viewMode === 'team' || this.viewMode === 'org') && this.canApplyOnBehalf && !this.canApplyOnBehalfAll && this.selectedTeamMember)
      )
    },
    hasActiveRegularizationFilters() {
      return this.regularizationSourceTypeFilter !== 'all' ||
             (this.regularizationDateFilter && Array.isArray(this.regularizationDateFilter) && this.regularizationDateFilter.length === 2) ||
             (this.regularizationEmployeeFilter != null && this.regularizationEmployeeFilter !== '') ||
             (this.regularizationLeaveTypeFilter != null && this.regularizationLeaveTypeFilter !== '') ||
             this.teamLeaveFilter !== 'all' ||
             this.teamRegularizationFilter !== 'all'
    },
    dynamicTeamStats() {
      // Calculate team stats dynamically based on selected Account Manager
      // If Account Manager is selected, filter by their team; otherwise use all team data
      const isShowingAllTeam = (this.viewMode === 'team' || this.viewMode === 'org') && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
      
      if (!isShowingAllTeam) {
        // Not showing team view, return zeros
        return {
          totalLeavesAvailed: 0,
          totalWFHCount: 0,
          totalRegularization: 0,
          membersOnLeave: 0,
        }
      }
      
      // Determine which team members to include based on selected Account Manager
      let teamMemberIds = []
      const amTeamIds = this.accountManagerTeamMemberIds
      if (amTeamIds && amTeamIds.length > 0) {
        teamMemberIds = amTeamIds
      } else if (this.teamMemberConfigs && this.teamMemberConfigs.length > 0) {
        teamMemberIds = this.teamMemberConfigs.map(config => config.emp_id)
      }
      
      // Calculate stats for the filtered team
      const todayMoment = moment()
      let totalLeavesAvailed = 0
      let totalWFHCount = 0
      let totalRegularization = 0
      let membersOnLeave = 0
      
      // Filter leaves by team member IDs
      const teamLeaves = teamMemberIds.length > 0
        ? this.allTeamLeaves.filter(leave => teamMemberIds.includes(leave.emp_id))
        : this.allTeamLeaves
      
      // Filter regularizations by team member IDs; exclude today's local calendar day
      const todayLocal = moment().format('YYYY-MM-DD')
      const isNotTodayReg = (reg) => {
        if (!reg) return false
        const caseDate = reg.datestring || reg.date
        if (!caseDate) return true
        return moment(caseDate).format('YYYY-MM-DD') !== todayLocal
      }
      const teamRegularizations = (teamMemberIds.length > 0
        ? (this.regularizationCases || []).filter(reg => teamMemberIds.includes(reg.emp_id))
        : (this.regularizationCases || [])
      ).filter(isNotTodayReg)
      
      // Calculate leaves availed (exclude WFH and CO-REQUEST from leave count)
      teamLeaves.forEach(leave => {
        const isApproved = (leave.status || '').toLowerCase() === 'approved'
        if (!isApproved) return

        if (typeof this.isExcludedFromLeaveKpi === 'function' && this.isExcludedFromLeaveKpi(leave)) {
          if (typeof this.isWFHLeave === 'function' && this.isWFHLeave(leave)) {
            totalWFHCount += leave.total_days || 1
          }
          return
        }

        totalLeavesAvailed += typeof this.getLeaveRecordDays === 'function'
          ? this.getLeaveRecordDays(leave)
          : (leave.total_days || 1)
      })
      
      // Count regularizations
      totalRegularization = teamRegularizations.length
      
      // Count members currently on leave (approved leaves that include today)
      const activeLeaves = teamLeaves.filter(leave => {
        if ((leave.status || '').toLowerCase() !== 'approved') return false
        const fromDate = moment(leave.from_date)
        const toDate = moment(leave.to_date)
        return todayMoment.isSameOrAfter(fromDate, 'day') && todayMoment.isSameOrBefore(toDate, 'day')
      })
      
      // Get unique employees on leave
      const uniqueEmployeesOnLeave = new Set(activeLeaves.map(leave => leave.emp_id))
      membersOnLeave = uniqueEmployeesOnLeave.size
      
      return {
        totalLeavesAvailed,
        totalWFHCount,
        totalRegularization,
        membersOnLeave,
      }
    },
    displayedTeamMembersCount() {
      // Return the count of team members based on selected Account Manager
      // If Account Manager is selected, show their team count; otherwise show all team members
      if ((this.viewMode === 'team' || this.viewMode === 'org') && this.selectedAccountManager) {
        if (this.accountManagerTeamMemberIds && this.accountManagerTeamMemberIds.length > 0) {
          return this.accountManagerTeamMemberIds.length
        }
        if (this.teamMemberConfigs) {
          return this.teamMemberConfigs.length
        }
      }
      return this.teamMembers.length
    },
    canViewTeam() {
      // Check if user has permission to view team view
      // Purely permission-based
      try {
        if (this.$can && typeof this.$can === 'function') {
          return this.$can('read', ATTENDANCE_PERMISSIONS.TEAM_DASHBOARD) || this.$can('read', 'team')
        }
        return false
      } catch (e) {
        return false
      }
    },
    canViewEmployee() {
      // Check if user has permission to view employee view
      // Anyone with 'read' permission on 'employee' can view the employee view
      try {
        return this.$can('read', 'employee')
      } catch (e) {
        return false
      }
    },
    canViewOrg() {
      // Check if user has permission to view organization-wide data
      // Matches backend: attendance_organization_dashboard OR approve leave_all
      try {
        if (this.$can && typeof this.$can === 'function') {
          return this.$can('read', 'attendance_organization_dashboard') || 
                 this.$can('approve', 'leave_all') ||
                 this.$can('manage', 'all')
        }
        return false
      } catch (e) {
        return false
      }
    },
    isHRDirector() {
      return this.$can('read', 'hr_director') 
    },
    isHR() {
      return this.$can('read', 'hr') 
    },
    isAccountManager() {
      return this.$can('read', 'account_manager') || this.$can('read', 'program_manager') 
    },
    isAdmin() {
      // Check if user is admin by role name or roleID
      const roleName = (this.userRole || '').toUpperCase()
      return roleName.includes('ADMIN') || 
             this.userRoleID === 1 || // Assuming 1 is admin roleID
             this.$can('read', 'admin')
    },
    isMaster() {
      // Check if user is master by role name or roleID
      const roleName = (this.userRole || '').toUpperCase()
      return roleName.includes('MASTER') || 
             this.userRoleID === 0 || // Assuming 0 is master roleID
             this.$can('read', 'master')
    },
    canManageLeaves() {
      // Show Approve/Reject for anyone who can approve at any stage (manager, HR, or leave_all in org)
      return this.$can('manage', 'all') ||
        this.$can('manage', 'leave') ||
        this.$can('apply_on_behalf', 'leave_all') ||
        this.isAdmin ||
        this.isMaster ||
        this.canApproveLeaveManagerStage ||
        this.canApproveLeaveHRStage ||
        this.canApproveLeaveAllStages ||
        this.canApproveRegularizationManagerStage ||
        this.canApproveRegularizationAllStages
    },
    canManageHolidays() {
      try {
        return this.$can('create', 'holiday') ||
               this.$can('manage', 'holiday') ||
               ['hr', 'hr-director', 'admin', 'master'].includes((this.userRole || '').toLowerCase())
      } catch (e) { return false }
    },
    canManageAnnouncements() {
      try {
        return this.$can('create', 'announcement') ||
               ['hr', 'hr-director', 'admin', 'master'].includes((this.userRole || '').toLowerCase())
      } catch (e) { return false }
    },
    // Mirrors canManageHolidays() exactly — same permission tier (HR/HR
    // Director/Admin/Master) manages Company-Wide WFH Days as manages
    // Holidays, since both are company-wide, date-level declarations.
    canManageCompanyWfhDays() {
      try {
        return this.$can('create', 'company_wfh_day') ||
               this.$can('manage', 'company_wfh_day') ||
               ['hr', 'hr-director', 'admin', 'master'].includes((this.userRole || '').toLowerCase())
      } catch (e) { return false }
    },
    
    // ============================================
    // NEW PERMISSION-BASED COMPUTED PROPERTIES
    // These replace role-based checks with permission-based checks
    // Backend should return these abilities in user's abilities array
    // ============================================
    
    /**
     * Check if user can view team dashboard
     * Purely permission-based
     */
    canViewTeamDashboard() {
      return this.$can('read', ATTENDANCE_PERMISSIONS.TEAM_DASHBOARD)
    },
    
    /**
     * Check if user can apply leave on behalf of reportees
     * Purely permission-based
     */
    canApplyOnBehalf() {
      return this.$can('apply_on_behalf', ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_LEAVE)
    },
    
    /**
     * Check if user can apply leave on behalf of any employee
     * Purely permission-based
     */
    canApplyOnBehalfAll() {
      return this.$can('apply_on_behalf', ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_ALL)
    },

    /**
     * True only when the Leave Application form's employee dropdown
     * (`getEmployeeOptionsForLeave`, which includes a "(Me)" option — see
     * `teamMemberOptions`) has been set to someone OTHER than the current
     * logged-in user. This — not `canApplyOnBehalf`/`canApplyOnBehalfAll`
     * alone — is what should gate whether `leaveCalendarDays` lets the
     * "Apply on Behalf" flow pick an already-past date: those two just say
     * the user is ALLOWED to pick a different employee at all; this says
     * they actually DID, for this specific form instance right now. A
     * manager applying for their own leave (picks themselves / leaves it
     * unset) must still be blocked from backdating, same as any employee.
     */
    isApplyingLeaveOnBehalf() {
      const selected = this.leaveForm && this.leaveForm.selected_employee
      return !!selected && String(selected) !== String(this.emp_id)
    },

    /** Same idea as `isApplyingLeaveOnBehalf`, for the WFH form. */
    isApplyingWfhOnBehalf() {
      const selected = this.wfhForm && this.wfhForm.selected_employee
      return !!selected && String(selected) !== String(this.emp_id)
    },

    /** Same idea as `isApplyingLeaveOnBehalf`, for the Comp Off (redeem) form. */
    isApplyingCompOffOnBehalf() {
      const selected = this.compOffForm && this.compOffForm.selected_employee
      return !!selected && String(selected) !== String(this.emp_id)
    },
    
    /**
     * Check if user can approve leaves at manager stage (first stage)
     * Purely permission-based
     */
    canApproveLeaveManagerStage() {
      return this.$can('approve', ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_MANAGER)
    },
    /**
     * Check if user can approve leaves at HR stage
     */
    canApproveLeaveHRStage() {
      return this.$can('approve', ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_HR)
    },
    /**
     * Check if user can approve leaves at any stage (final approver).
     * Only in org view; in team/my view leave_all cannot approve any stage.
     */
    canApproveLeaveAllStages() {
      if (this.viewMode !== 'org') return false
      return this.$can('manage', 'all') || this.$can('approve', ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_ALL)
    },
    
    /**
     * Check if user can approve regularization at manager stage
     * Purely permission-based
     */
    canApproveRegularizationManagerStage() {
      return this.$can('approve', ATTENDANCE_PERMISSIONS.APPROVE_REGULARIZATION_MANAGER)
    },
    
    /**
     * Check if user can approve regularization at any stage (final approver)
     * Purely permission-based
     */
    canApproveRegularizationAllStages() {
      return this.$can('manage', 'all') || this.$can('approve', ATTENDANCE_PERMISSIONS.APPROVE_REGULARIZATION_ALL)
    },
    /**
     * Can apply regularization (own or on behalf in team/org). Used for showing Regularize button when status is Pending.
     */
    canApplyRegularization() {
      return this.$can('manage', 'all') ||
        this.$can('apply', 'regularization') ||
        this.$can('apply_on_behalf', ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_REGULARIZATION) ||
        this.$can('apply_on_behalf', ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_REGULARIZATION_ALL)
    },
    /**
     * Check if user can manage attendance configuration
     * Purely permission-based
     */
    canManageAttendanceConfig() {
      return this.$can('manage', ATTENDANCE_PERMISSIONS.MANAGE_CONFIG)
    },

    /**
     * Download button on Announcements / HR panel (Birthdays & Anniversaries CSV).
     * Issue on login (preferred): { action: 'download', subject: 'announcement' }
     * Also accepted: create/manage on announcement (existing HR create ability);
     * read+manage on announcement; legacy download on attendance_celebrations; manage all.
     */
    canDownloadAnnouncements() {
      try {
        return this.$can('download', 'announcement') ||
               this.$can('download', 'announcements') ||
               this.$can('create', 'announcement') ||
               this.$can('manage', 'announcement') ||
               (this.$can('read', 'announcement') && this.$can('manage', 'announcement')) ||
               this.$can('download', ATTENDANCE_PERMISSIONS.DOWNLOAD_CELEBRATIONS) ||
               this.$can('manage', 'all')
      } catch (e) {
        return false
      }
    },
    /** @deprecated Use canDownloadAnnouncements — same gate for the HR panel download button */
    canDownloadBirthdaysAnniversaries() {
      return this.canDownloadAnnouncements
    },

    /**
     * Human-readable label shown in the AnnouncementsCard download button tooltip.
     * Shows the chosen download range, or "Current Month" as default.
     */
    celebrationDownloadDurationLabel() {
      if (this.celebrationDownloadFrom && this.celebrationDownloadTo) {
        const from = moment(this.celebrationDownloadFrom).format('DD MMM YYYY')
        const to = moment(this.celebrationDownloadTo).format('DD MMM YYYY')
        return `${from} – ${to}`
      }
      return moment().format('MMMM YYYY')
    },
    celebrationRangeSummary() {
      if (!this.celebrationDownloadFrom || !this.celebrationDownloadTo) return ''
      const from = moment(this.celebrationDownloadFrom).format('DD MMM YYYY')
      const to   = moment(this.celebrationDownloadTo).format('DD MMM YYYY')
      return `${from}  →  ${to}`
    },
    punchDownloadRangeSummary() {
      if (!this.punchDownloadFrom || !this.punchDownloadTo) return ''
      const from = moment(this.punchDownloadFrom).format('DD MMM YYYY')
      const to   = moment(this.punchDownloadTo).format('DD MMM YYYY')
      return `${from}  →  ${to}`
    },
    
    /**
     * Map emp_id -> department from teamMembers and teamWiseEmployees (for applicant department resolution)
     */
    employeeDepartmentMap() {
      const map = {}
      const sources = [...(this.teamMembers || []), ...(this.teamWiseEmployees || [])]
      sources.forEach(emp => {
        const id = emp.emp_id || emp.id
        const dept = emp.department != null && emp.department !== '' ? String(emp.department).trim() : null
        if (id && dept) map[id] = dept
      })
      return map
    },
    // ============================================
    // END PERMISSION-BASED COMPUTED PROPERTIES
    // ============================================
    
    accountManagerOptions() {
      return this.accountManagers.map(manager => {
        const empId = manager.emp_id || manager.id
        const name = this.getUserName(empId, manager)
        return {
          value: empId,
          text: name,
        }
      })
    },
    regularizationFilterEmployeeOptions() {
      const base = this.teamWiseEmployeeOptions || []
      return [{ value: null, text: 'All Employees' }, ...base]
    },
    teamWiseEmployeeOptions() {
      // Use employees from modal if in filter modal, otherwise use regular employees
      let employeesToUse = (this.showFilterModal && this.teamWiseEmployeesForModal.length > 0) 
        ? this.teamWiseEmployeesForModal 
        : this.teamWiseEmployees
      
      // If no teamWiseEmployees (e.g., when viewing all employees without account manager selected),
      // fall back to teamMembers which contains all employees for super approvers
      if ((!employeesToUse || employeesToUse.length === 0) && this.canApplyOnBehalfAll) {
        employeesToUse = this.teamMembers || []
      }
      
      if (!employeesToUse || employeesToUse.length === 0) {
        return []
      }
      
      return employeesToUse.map(employee => {
        const empId = employee.emp_id || employee.id
        // Use employee object directly to get name - prioritize first_name/last_name
        let name = empId // fallback to emp_id
        if (employee.first_name) {
          name = `${employee.first_name} ${employee.last_name || ''}`.trim()
        } else if (employee.name) {
          name = employee.name
        } else {
          // Try getUserName as fallback
          name = this.getUserName(empId, employee)
        }
        return {
          value: empId,
          text: name || empId,
        }
      })
    },
    teamMemberOptions() {
      // Include self in team member options
      const options = [
        { value: this.emp_id, text: `${this.getUserName(this.emp_id)} (Me)` }
      ]
      // Add team members
      this.teamMembers.forEach(member => {
        const memberEmpId = member.emp_id || member.id
        if (memberEmpId && memberEmpId !== this.emp_id) {
          options.push({
            value: memberEmpId,
            text: this.getUserName(memberEmpId, member)
          })
        }
      })
      return options
    },
    getEmployeeOptionsForLeave() {
      // Return employee options for leave form dropdown
      if (this.canApplyOnBehalfAll) {
        // Super approvers can see all employees
        return this.teamWiseEmployeeOptions || []
      } else if (this.canApplyOnBehalf) {
        // Managers can see their team members
        return this.teamMemberOptions || []
      }
      return []
    },
    getEmployeeOptionsForWFH() {
      // Return employee options for WFH form dropdown
      if (this.canApplyOnBehalfAll) {
        // Super approvers can see all employees
        return this.teamWiseEmployeeOptions || []
      } else if (this.canApplyOnBehalf) {
        // Managers can see their team members
        return this.teamMemberOptions || []
      }
      return []
    },
    getEmployeeOptionsForCompOff() {
      // Return employee options for Comp Off form dropdown
      if (this.canApplyOnBehalfAll) {
        // Super approvers can see all employees
        const options = this.teamWiseEmployeeOptions || []
        return Array.isArray(options) ? options : []
      } else if (this.canApplyOnBehalf) {
        // Managers can see their team members
        const options = this.teamMemberOptions || []
        return Array.isArray(options) ? options : []
      }
      return []
    },
    displayCompOffCalendarMonthYear() {
      return moment().month(this.compOffCalendarMonth).year(this.compOffCalendarYear).format('MMMM YYYY')
    },
    displayCompOffWorkCalendarMonthYear() {
      return moment().month(this.compOffWorkCalendarMonth).year(this.compOffWorkCalendarYear).format('MMMM YYYY')
    },
    compOffCalendarDays() {
      const days = []
      const startOfMonth = moment().month(this.compOffCalendarMonth).year(this.compOffCalendarYear).startOf('month')
      const endOfMonth = moment().month(this.compOffCalendarMonth).year(this.compOffCalendarYear).endOf('month')
      const startDate = startOfMonth.clone().startOf('week')
      const endDate = endOfMonth.clone().endOf('week')
      
      let currentDate = startDate.clone()
      const today = moment().startOf('day')
      const selectedDate = this.compOffForm.date ? moment(this.compOffForm.date).startOf('day') : null
      
      // Build disabled dates set (already applied leaves and holidays)
      const disabledDatesSet = new Set()
      
      // Add already applied dates
      this.myLeaves.forEach(leave => {
        if (!leave.from_date || !leave.to_date) return
        const leaveFrom = moment(leave.from_date).startOf('day')
        const leaveTo = moment(leave.to_date).startOf('day')
        
        if (leave.status === 'Approved' || 
            leave.status === 'Pending Manager Approval' || 
            leave.status === 'Manager Approved - Pending HR') {
          let checkDate = leaveFrom.clone()
          while (checkDate.isSameOrBefore(leaveTo, 'day')) {
            if (checkDate.isSameOrAfter(today, 'day')) {
              disabledDatesSet.add(checkDate.format('YYYY-MM-DD'))
            }
            checkDate.add(1, 'day')
          }
        }
      })
      
      // Add holidays
      this.holidays.forEach(holiday => {
        const holidayDate = moment(holiday.holiday_date || holiday.date).startOf('day')
        if (holidayDate.isSameOrAfter(today, 'day')) {
          disabledDatesSet.add(holidayDate.format('YYYY-MM-DD'))
        }
      })
      
      while (currentDate.isSameOrBefore(endDate, 'day')) {
        const dateStr = currentDate.format('YYYY-MM-DD')
        const isCurrentMonth = currentDate.month() === this.compOffCalendarMonth
        const isToday = currentDate.isSame(today, 'day')
        const isPast = currentDate.isBefore(today, 'day')
        
        const isAlreadyApplied = disabledDatesSet.has(dateStr)
        const isHoliday = this.holidays.some(h => {
          const holidayDate = moment(h.holiday_date || h.date).format('YYYY-MM-DD')
          return holidayDate === dateStr
        })
        // Backdating (past dates) is only allowed when this Comp Off form is
        // being filed on behalf of someone else — see `isApplyingCompOffOnBehalf`.
        const isDisabled = (isPast && !this.isApplyingCompOffOnBehalf) || isAlreadyApplied || isHoliday
        const isSelected = selectedDate && currentDate.isSame(selectedDate, 'day')
        
        days.push({
          date: currentDate.date(),
          dateStr,
          isCurrentMonth,
          isToday,
          isPast,
          isSelected,
          isDisabled,
          isAlreadyApplied,
          isHoliday,
        })
        
        currentDate.add(1, 'day')
      }
      
      return days
    },
    compOffBalanceWorkCalendarDays() {
      // Calendar for selecting work date for CO balance request (past dates only)
      const days = []
      const startOfMonth = moment().month(this.compOffBalanceWorkCalendarMonth).year(this.compOffBalanceWorkCalendarYear).startOf('month')
      const endOfMonth = moment().month(this.compOffBalanceWorkCalendarMonth).year(this.compOffBalanceWorkCalendarYear).endOf('month')
      const startDate = startOfMonth.clone().startOf('week')
      const endDate = endOfMonth.clone().endOf('week')
      
      let currentDate = startDate.clone()
      const today = moment().startOf('day')
      const selectedDate = this.compOffBalanceRequestForm.work_date ? moment(this.compOffBalanceRequestForm.work_date).startOf('day') : null
      
      while (currentDate.isSameOrBefore(endDate, 'day')) {
        const dateStr = currentDate.format('YYYY-MM-DD')
        const isCurrentMonth = currentDate.month() === this.compOffBalanceWorkCalendarMonth
        const isToday = currentDate.isSame(today, 'day')
        const isFuture = currentDate.isAfter(today, 'day')
        const isPast = currentDate.isBefore(today, 'day')
        
        // For balance request work date, disable future dates (can only claim CO for past work)
        const isDisabled = isFuture
        const isSelected = selectedDate && currentDate.isSame(selectedDate, 'day')
        
        days.push({
          date: currentDate.date(),
          dateStr,
          isCurrentMonth,
          isToday,
          isFuture,
          isPast,
          isSelected,
          isDisabled,
        })
        
        currentDate.add(1, 'day')
      }
      
      return days
    },
    displayCompOffBalanceWorkCalendarMonthYear() {
      return moment().month(this.compOffBalanceWorkCalendarMonth).year(this.compOffBalanceWorkCalendarYear).format('MMMM YYYY')
    },
    compOffWorkCalendarDays() {
      // Calendar for selecting work date (can be past dates, should be weekends/holidays)
      const days = []
      const startOfMonth = moment().month(this.compOffWorkCalendarMonth).year(this.compOffWorkCalendarYear).startOf('month')
      const endOfMonth = moment().month(this.compOffWorkCalendarMonth).year(this.compOffWorkCalendarYear).endOf('month')
      const startDate = startOfMonth.clone().startOf('week')
      const endDate = endOfMonth.clone().endOf('week')
      
      let currentDate = startDate.clone()
      const today = moment().startOf('day')
      const selectedDate = this.compOffForm.work_date ? moment(this.compOffForm.work_date).startOf('day') : null
      
      while (currentDate.isSameOrBefore(endDate, 'day')) {
        const dateStr = currentDate.format('YYYY-MM-DD')
        const isCurrentMonth = currentDate.month() === this.compOffWorkCalendarMonth
        const isToday = currentDate.isSame(today, 'day')
        const isFuture = currentDate.isAfter(today, 'day')
        
        // For work date, disable future dates (can only claim comp off for past work)
        const isDisabled = isFuture
        const isSelected = selectedDate && currentDate.isSame(selectedDate, 'day')
        
        days.push({
          date: currentDate.date(),
          dateStr,
          isCurrentMonth,
          isToday,
          isFuture,
          isSelected,
          isDisabled,
        })
        
        currentDate.add(1, 'day')
      }
      
      return days
    },
    monthOptions() {
      return moment.months().map((month, index) => ({
        value: index,
        text: month,
      }))
    },
    yearOptions() {
      const currentYear = moment().year()
      const years = []
      for (let i = currentYear - 2; i <= currentYear + 2; i++) {
        years.push({ value: i, text: i.toString() })
      }
      return years
    },
    displayedAnnouncements() {
      // Combine announcements, birthdays, and work anniversaries
      
      // Get birthdays and work_anniversaries from dashboardData if available, otherwise use legacy properties (ensure arrays)
      const rawBirthdays = this.dashboardData?.birthdays ?? this.birthdays
      const birthdaysData = Array.isArray(rawBirthdays) ? rawBirthdays : []
      const rawAnniversaries = this.dashboardData?.work_anniversaries ?? this.workAnniversaries
      const workAnniversariesData = Array.isArray(rawAnniversaries) ? rawAnniversaries : []
      
      // Define range: Next 20 days (inclusive)
      const today = moment().startOf('day')
      const endLimit = moment().add(20, 'days').endOf('day')

      // Filter announcements: ACTIVE and within next 20 days
      const activeAnnouncements = this.announcements.filter(a => {
        const isActive = (a.status || 'ACTIVE') === 'ACTIVE'
        if (!isActive) return false
        const date = moment(a.date)
        return date.isBetween(today, endLimit, 'day', '[]')
      })
      
      // Format birthdays with title and message
      const formattedBirthdays = birthdaysData.map(b => {
        const birthdayDate = moment(b.date)
        // Calculate upcoming birthday date (this year or next year)
        const thisYearBirthday = moment().year(moment().year()).month(birthdayDate.month()).date(birthdayDate.date())
        const displayDate = thisYearBirthday.isBefore(today) 
          ? thisYearBirthday.clone().add(1, 'year') 
          : thisYearBirthday.clone()
        
        return {
          ...b,
          type: 'birthday',
          title: `🎉 ${b.name}'s Birthday`,
          message: `${b.name} is celebrating their birthday`,
          birthday_date: b.date,
          date: displayDate.format('YYYY-MM-DD')
        }
      }).filter(b => {
        // Filter to show only birthdays within next 20 days
        const displayDate = moment(b.date)
        return displayDate.isBetween(today, endLimit, 'day', '[]')
      })
      
      // Format work anniversaries with title and message
      const formattedAnniversaries = workAnniversariesData.map(w => {
        const anniversaryDate = moment(w.date)
        // Calculate upcoming anniversary date (this year or next year)
        const thisYearAnniversary = moment().year(moment().year()).month(anniversaryDate.month()).date(anniversaryDate.date())
        const displayDate = thisYearAnniversary.isBefore(today) 
          ? thisYearAnniversary.clone().add(1, 'year') 
          : thisYearAnniversary.clone()
        
        const yearsText = w.years === 1 ? '1 year' : `${w.years} years`
        
        return {
          ...w,
          type: 'anniversary',
          title: `🎊 ${w.name}'s Work Anniversary`,
          message: `${w.name} is completing ${yearsText} with us`,
          anniversary_date: w.date,
          date: displayDate.format('YYYY-MM-DD')
        }
      }).filter(w => {
        // Filter to show only anniversaries within next 20 days
        const displayDate = moment(w.date)
        return displayDate.isBetween(today, endLimit, 'day', '[]')
      })
      
      const allItems = [
        ...activeAnnouncements.map(a => ({ ...a, type: 'announcement' })),
        ...formattedBirthdays,
        ...formattedAnniversaries,
      ]
      // Sort by date Ascending (Oldest/Soonest first)
      allItems.sort((a, b) => {
        const dateA = moment(a.date || a.created_at || a.birthday_date || a.anniversary_date)
        const dateB = moment(b.date || b.created_at || b.birthday_date || b.anniversary_date)
        return dateA.diff(dateB) // Ascending: A - B
      })
      return allItems
    },
    totalAnnouncementsCount() {
      const birthdaysCount = (this.dashboardData?.birthdays || this.birthdays || []).length
      const workAnniversariesCount = (this.dashboardData?.work_anniversaries || this.workAnniversaries || []).length
      return this.announcements.length + birthdaysCount + workAnniversariesCount
    },
    regularizationFields() {
      const baseFields = [
        { key: 'datestring', label: 'Date' },
        { key: 'issue_type', label: 'Issue' },
        { key: 'data_source_status', label: 'Data Source' },
        { key: 'punch_in_time', label: 'Punch IN' },
        { key: 'punch_out_time', label: 'Punch OUT' },
        // { key: 'intime', label: 'IN Time' },
        // { key: 'outtime', label: 'OUT Time' },
        { key: 'work_hours', label: 'Work Hours' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ]
      
      // Add Employee column and Remarks by Manager in Team View or Org View for those who can view team
      if ((this.viewMode === 'team' || this.viewMode === 'org') && this.canViewTeamDashboard) {
        // Employee Name before Date, Remarks by Manager before Actions
        return [
          { key: 'employee', label: 'Employee' }, // Employee Name first
          baseFields[0], // Date
          baseFields[1], // Issue
          baseFields[2], // Data Source
          baseFields[3], // Punch IN
          baseFields[4], // Punch OUT
          baseFields[5], // IN Time
          baseFields[6], // OUT Time
          baseFields[7], // Work Hours
          { key: 'status', label: 'Status' }, // Status
          baseFields[baseFields.length - 1], // Actions last
        ]
      }
      
      return baseFields
    },
    leavesTabFields() {
      // Leaves tab: Type (not "Issue"), no Data Source column (always "Leave" — noise for managers)
      const base = [
        { key: 'from_date', label: 'From' },
        { key: 'to_date', label: 'To' },
        { key: 'total_days', label: 'Days' },
        { key: 'issue_type', label: 'Type' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ]
      if ((this.viewMode === 'team' || this.viewMode === 'org') && this.canViewTeamDashboard) {
        return [
          { key: 'employee', label: 'Employee' },
          { key: 'from_date', label: 'From' },
          { key: 'to_date', label: 'To' },
          { key: 'total_days', label: 'Days' },
          { key: 'issue_type', label: 'Type' },
          { key: 'status', label: 'Status' },
          { key: 'actions', label: 'Actions' },
        ]
      }
      return base
    },
    unifiedRegularizationData() {
      // Get leaves based on view mode
      let leavesToTransform = []
      
      // Safety check: ensure myLeaves and allTeamLeaves are arrays
      const myLeavesArray = Array.isArray(this.myLeaves) ? this.myLeaves : []
      const allTeamLeavesArray = Array.isArray(this.allTeamLeaves) ? this.allTeamLeaves : []
      const regularizationCasesArray = Array.isArray(this.regularizationCases) ? this.regularizationCases : []
      
      if (this.viewMode === 'team' || this.viewMode === 'org') {
        // In Team View or Org View, use allTeamLeaves or filtered myLeaves
        const isShowingAllTeam = (
          (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
          (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
          (this.canApplyOnBehalfAll && !this.selectedAccountManager)
        )
        
        if (isShowingAllTeam) {
          leavesToTransform = allTeamLeavesArray
          
          // Filter by Account Manager's team when Account Manager is selected (for HR/HR Director/Admin/Master)
          if (this.selectedAccountManager && this.canApplyOnBehalfAll && this.teamWiseEmployees && this.teamWiseEmployees.length > 0) {
            const teamMemberIds = this.teamWiseEmployees.map(emp => emp.emp_id || emp.id).filter(id => id)
            if (teamMemberIds.length > 0) {
              leavesToTransform = leavesToTransform.filter(leave => leave && teamMemberIds.includes(leave.emp_id))
            }
          }
        } else if (this.selectedTeamMember) {
          // HYPOTHESIS C: Use allTeamLeavesArray, not myLeavesArray for filtering by selected employee
          leavesToTransform = allTeamLeavesArray.filter(leave => leave && leave.emp_id === this.selectedTeamMember)
        } else if (this.selectedTeamWiseEmployee) {
          leavesToTransform = allTeamLeavesArray.filter(leave => leave && leave.emp_id === this.selectedTeamWiseEmployee)
        } else {
          leavesToTransform = myLeavesArray.filter(leave => {
            if (!leave) return false
            // Personal dashboard leaves often omit emp_id — keep them for the signed-in user
            if (!leave.emp_id) return true
            return leave.emp_id === this.emp_id
          })
        }
      } else {
        // In Employee View, use myLeaves (emp_id may be omitted on personal dashboard payload)
        const targetEmpId = this.currentViewEmpId || this.emp_id
        leavesToTransform = myLeavesArray.filter(leave => {
          if (!leave) return false
          if (!leave.emp_id) return true
          return String(leave.emp_id) === String(targetEmpId)
        })
      }
      
      // Transform leaves to regularization format
      const transformedLeaves = this.transformLeavesToRegularizationFormat(leavesToTransform)
      
      // Add source_type to regularization cases and filter by employee if needed
      let regularizationCasesWithSource = regularizationCasesArray.map(caseItem => ({
        ...caseItem,
        source_type: 'Regularization',
      }))
      
      // Filter regularization cases by employee based on view mode
      const targetEmpId = this.currentViewEmpId || this.emp_id
      if (this.viewMode === 'team' || this.viewMode === 'org') {
        if (this.selectedTeamMember) {
          regularizationCasesWithSource = regularizationCasesWithSource.filter(reg => reg && reg.emp_id === this.selectedTeamMember)
        } else if (this.selectedTeamWiseEmployee) {
          regularizationCasesWithSource = regularizationCasesWithSource.filter(reg => reg && reg.emp_id === this.selectedTeamWiseEmployee)
        } else if (this.selectedAccountManager && this.canApplyOnBehalfAll && this.teamWiseEmployees && this.teamWiseEmployees.length > 0) {
          // Filter by Account Manager's team when Account Manager is selected (for HR/HR Director/Admin/Master)
          const teamMemberIds = this.teamWiseEmployees.map(emp => emp.emp_id || emp.id).filter(id => id)
          if (teamMemberIds.length > 0) {
            regularizationCasesWithSource = regularizationCasesWithSource.filter(reg => reg && reg.emp_id && teamMemberIds.includes(reg.emp_id))
          }
        }
      } else {
        // In Employee View, filter regularizations by own emp_id
        regularizationCasesWithSource = regularizationCasesWithSource.filter(reg => reg && reg.emp_id === targetEmpId)
      }

      // Exclude today's local calendar day from regularization list (all views)
      const todayLocal = moment().format('YYYY-MM-DD')
      regularizationCasesWithSource = regularizationCasesWithSource.filter(reg => {
        if (!reg) return false
        const caseDate = reg.datestring || reg.date
        if (!caseDate) return true
        return moment(caseDate).format('YYYY-MM-DD') !== todayLocal
      })

      // Merge regularization cases and transformed leaves
      const merged = [...regularizationCasesWithSource, ...transformedLeaves]
      
      // Apply source type filter (client-side only, since this merges two different data sources)
      // Date and leave type filters are handled by backend API
      let filtered = merged
      if (this.regularizationSourceTypeFilter === 'regularization') {
        filtered = filtered.filter(item => item.source_type === 'Regularization')
      } else if (this.regularizationSourceTypeFilter === 'leave') {
        filtered = filtered.filter(item => item.source_type === 'Leave')
      }
      // Apply employee filter (Leaves & Regularization filter modal)
      if (this.regularizationEmployeeFilter != null && this.regularizationEmployeeFilter !== '') {
        const empId = String(this.regularizationEmployeeFilter)
        filtered = filtered.filter(item => item && String(item.emp_id) === empId)
      }
      // Apply leave type filter (Leaves & Regularization filter modal) — client-side so both Leaves and Regularization respect it
      if (this.regularizationLeaveTypeFilter != null && this.regularizationLeaveTypeFilter !== '') {
        const selectedLeaveType = String(this.regularizationLeaveTypeFilter)
        filtered = filtered.filter(item => {
          if (!item) return false
          const itemType = String(item.leave_type || '')
          if (itemType === selectedLeaveType) return true
          if (isWFHLeaveType(selectedLeaveType) && isWFHLeaveType(itemType)) return true
          return false
        })
      }
      // Apply date range filter (Leaves & Regularization filter modal)
      if (this.regularizationDateFilter && Array.isArray(this.regularizationDateFilter) && this.regularizationDateFilter.length === 2) {
        const startDate = moment(this.regularizationDateFilter[0], 'YYYY-MM-DD', true)
        const endDate = moment(this.regularizationDateFilter[1], 'YYYY-MM-DD', true)
        if (startDate.isValid() && endDate.isValid()) {
          filtered = filtered.filter(item => {
            if (!item) return false
            if (item.source_type === 'Leave') {
              let leaveFromDate = moment(item.from_date || item.datestring || item.date, 'YYYY-MM-DD', true)
              let leaveToDate = moment(item.to_date || item.from_date || item.datestring || item.date, 'YYYY-MM-DD', true)
              if (!leaveFromDate.isValid()) {
                leaveFromDate = moment(item.from_date || item.datestring || item.date)
              }
              if (!leaveToDate.isValid()) {
                leaveToDate = leaveFromDate.isValid() ? leaveFromDate : moment(item.to_date || item.from_date || item.datestring || item.date)
              }
              if (!leaveFromDate.isValid()) return false
              const leaveTo = leaveToDate.isValid() ? leaveToDate : leaveFromDate
              return (leaveFromDate.isSameOrAfter(startDate, 'day') && leaveFromDate.isSameOrBefore(endDate, 'day'))
                || (leaveTo.isSameOrAfter(startDate, 'day') && leaveTo.isSameOrBefore(endDate, 'day'))
                || (leaveFromDate.isBefore(startDate, 'day') && leaveTo.isAfter(endDate, 'day'))
            }
            const caseDate = moment(item.datestring || item.date, 'YYYY-MM-DD', true)
            const resolvedCaseDate = caseDate.isValid() ? caseDate : moment(item.datestring || item.date)
            if (!resolvedCaseDate.isValid()) return false
            return resolvedCaseDate.isSameOrAfter(startDate, 'day') && resolvedCaseDate.isSameOrBefore(endDate, 'day')
          })
        }
      }
      
      // Apply Status Filter (e.g. Pending Approvals)
      // Team/Org managers: use the same actionable predicates as Leave approvals / Reg queues
      // so badge count and table rows stay aligned (plain "pending", OH stage, etc.).
      if (this.teamLeaveFilter === 'pending' || this.teamRegularizationFilter === 'pending') {
        filtered = filtered.filter(item => {
          if (!item) return false
          const status = (item.status || item.regularization_status || '').toLowerCase().trim()
          if (status === 'approved' || status === 'rejected' || status === 'cancelled' ||
              status === 'hr reject' || status.includes('reject') || status.includes('cancel')) {
            return false
          }

          if ((this.viewMode === 'team' || this.viewMode === 'org') && this.canManageLeaves) {
            if (item.source_type === 'Leave') {
              return this.isLeaveActionableForCurrentUser(item)
            }
            if (item.source_type === 'Regularization') {
              return this.isRegularizationActionableForCurrentUser(item)
            }
            return false
          }

          // Employee / non-manager: any pending-stage status
          return status === 'pending' || status.includes('pending')
        })
      }
      
      // Date/leave type also re-filter client-side on merged leaves + regularization rows
      // (backend APIs may not apply the same range rules to both sources)
      
      // Sort by date (most recent first)
      return filtered.sort((a, b) => {
        const dateA = moment(a.datestring || a.date)
        const dateB = moment(b.datestring || b.date)
        return dateB.diff(dateA)
      })
    },
    displayedRegularizationData() {
      return this.regularizationData
    },
    displayedLeavesData() {
      return this.leavesData
    },
    leavesData() {
      // Filter unifiedRegularizationData to show only leaves (includes WFH / Work From Home)
      return this.unifiedRegularizationData.filter(item => item.source_type === 'Leave')
    },
    regularizationData() {
      // Filter unifiedRegularizationData to show only regularization cases
      return this.unifiedRegularizationData.filter(item => item.source_type === 'Regularization')
    },
    leaveTypeFilterOptions() {
      // Extract unique leave types from leaves and regularization data
      const leaveTypes = new Set()
      
      // Get leave types from myLeaves
      if (Array.isArray(this.myLeaves)) {
        this.myLeaves.forEach(leave => {
          if (leave.leave_type) {
            leaveTypes.add(leave.leave_type)
          }
        })
      }
      
      // Get leave types from allTeamLeaves
      if (Array.isArray(this.allTeamLeaves)) {
        this.allTeamLeaves.forEach(leave => {
          if (leave.leave_type) {
            leaveTypes.add(leave.leave_type)
          }
        })
      }
      
      // Get leave types from regularization cases
      if (Array.isArray(this.regularizationCases)) {
        this.regularizationCases.forEach(caseItem => {
          if (caseItem.leave_type) {
            leaveTypes.add(caseItem.leave_type)
          }
        })
      }
      
      // Convert to options array, sorted alphabetically
      return Array.from(leaveTypes)
        .sort()
        .map(leaveType => ({
          value: leaveType,
          text: leaveType
        }))
    },
    showLeaveTypeInRegularization() {
      // Always show leave type option in regularization modal
      return true
    },
    showInTimeField() {
      const leaveType = this.regularizationForm.leave_type
      if (leaveType && !requiresRegularizationTime(leaveType, this.leaveTypesFromDB)) return false
      
      if (!this.selectedRegularizationItem) return true
      // Show IN Time field if:
      // 1. IN time is missing (null, empty, or '0:00:00')
      // 2. Both are missing (NO Punch Data found)
      // 3. Both exist but work hours are less (Less Work Hours - user can update)
      const hasInTime = this.selectedRegularizationItem.intime && 
                        this.selectedRegularizationItem.intime !== '0:00:00' && 
                        this.selectedRegularizationItem.intime.trim() !== ''
      const hasOutTime = this.selectedRegularizationItem.outtime && 
                         this.selectedRegularizationItem.outtime !== '0:00:00' && 
                         this.selectedRegularizationItem.outtime.trim() !== ''
      const issueType = (this.selectedRegularizationItem.issue_type || '').trim()
      
      // If IN time is missing, show the field
      if (!hasInTime) return true
      
      // If both exist but issue is "Less Work Hours", show both fields
      if (hasInTime && hasOutTime && issueType === 'Less Work Hours') return true
      
      // If IN time exists but OUT time is missing, don't show IN time field
      if (hasInTime && !hasOutTime) return false
      
      // Default: show the field
      return true
    },
    showOutTimeField() {
      const leaveType = this.regularizationForm.leave_type
      if (leaveType && !requiresRegularizationTime(leaveType, this.leaveTypesFromDB)) return false
      
      if (!this.selectedRegularizationItem) return true
      // Show OUT Time field if:
      // 1. OUT time is missing (null, empty, or '0:00:00')
      // 2. Both are missing (NO Punch Data found)
      // 3. Both exist but work hours are less (Less Work Hours - user can update)
      const hasInTime = this.selectedRegularizationItem.intime && 
                        this.selectedRegularizationItem.intime !== '0:00:00' && 
                        this.selectedRegularizationItem.intime.trim() !== ''
      const hasOutTime = this.selectedRegularizationItem.outtime && 
                         this.selectedRegularizationItem.outtime !== '0:00:00' && 
                         this.selectedRegularizationItem.outtime.trim() !== ''
      const issueType = (this.selectedRegularizationItem.issue_type || '').trim()
      
      // If OUT time is missing, show the field
      if (!hasOutTime) return true
      
      // If both exist but issue is "Less Work Hours", show both fields
      if (hasInTime && hasOutTime && issueType === 'Less Work Hours') return true
      
      // If OUT time exists but IN time is missing, don't show OUT time field
      if (!hasInTime && hasOutTime) return false
      
      // Default: show the field
      return true
    },
    filteredLeaveTypeOptions() {
      // Priority order: 
      // 1. Global leave types from DB (leaveTypeOptions - populated from /api/leave-types)
      // 2. Employee-specific API options (apiLeaveTypeOptions) - fallback
      // Changed priority: prefer leaveTypeOptions from /api/leave-types as primary source
      let options = this.leaveTypeOptions.length > 0 
        ? this.leaveTypeOptions 
        : this.apiLeaveTypeOptions
      
      // Filter out types not applicable for regular leave application
      // - 'Present' is for regularization only
      // - 'WFH' has its own modal
      // - 'CO' (Compensatory Off) has its own modal
      // - '1st Half Day' / '2nd Half Day' are half-day modifiers, not standalone leave types
      options = options.filter(option => {
        const value = (option.value || option).toString().toUpperCase()
        const text = (option.text || option).toString().toLowerCase()
        
        // Exclude regularization-only types and types with dedicated modals
        return value !== 'PRESENT' && 
               !text.includes('present') &&
               value !== 'WFH' &&
               !text.includes('work from home') &&
               value !== 'CO' &&
               !text.includes('compensatory') &&
               value !== '1ST HALF DAY' &&
               value !== '2ND HALF DAY' &&
               !text.includes('1st half day') &&
               !text.includes('2nd half day')
      })
      
      // Enrich options with id from leaveTypesFromDB if not present
      options = options.map(opt => {
        if (opt.id) return opt // Already has id
        
        // Try to find matching leave type in leaveTypesFromDB
        const foundType = this.leaveTypesFromDB.find(lt => 
          lt.code === opt.value || 
          lt.display_name === opt.value ||
          lt.display_name === opt.text ||
          (lt.code && opt.value && lt.code.toUpperCase() === opt.value.toUpperCase())
        )
        
        if (foundType) {
          return {
            ...opt,
            id: foundType.id,
            code: foundType.code
          }
        }
        return opt
      })
      
      // Sort options to put "Privilege Leave" (PL) at the top
      options.sort((a, b) => {
        const aValue = (a.value || a).toString().toUpperCase()
        const bValue = (b.value || b).toString().toUpperCase()
        const aText = (a.text || a).toString().toLowerCase()
        const bText = (b.text || b).toString().toLowerCase()
        
        // Privilege Leave should be first
        if (aValue === 'PL' || aText.includes('privilege')) return -1
        if (bValue === 'PL' || bText.includes('privilege')) return 1
        
        // Otherwise maintain original order
        return 0
      })
      
      // Add placeholder option at the beginning if not present
      if (options.length > 0 && options[0].value !== null && options[0].value !== '') {
        return [{ value: null, text: 'Select leave type' }, ...options]
      }
      return options
    },
    /**
     * Check if the currently selected leave type supports half-day leaves.
     * Resolution order:
     *   1. Name-based exclusion: Maternity / Paternity never support half-day.
     *   2. leaveTypeOptions (populated from /api/leave-types, canBeHalfDay field) — only
     *      used to promote an explicit `true` value; a DB `false` does NOT override step 1.
     *   3. leaveTypesFromDB (can_be_half_day field) — same rule as above.
     *   4. Default: all non-excluded leave types support half-day (covers Privilege Leave,
     *      Sick Leave, Casual Leave, PTO, etc. regardless of what the DB stores).
     */
    selectedLeaveTypeSupportsHalfDay() {
      if (!this.leaveForm.leave_type) return false

      // Step 1 – name-based hard exclusions (Maternity / Paternity never support half-day)
      const leaveTypeLower = this.leaveForm.leave_type.toLowerCase()
      if (leaveTypeLower.includes('maternity') || leaveTypeLower.includes('paternity')) {
        return false
      }

      // Returns true only when the flag is explicitly enabled; null otherwise.
      const isExplicitlyEnabled = val =>
        val === true || val === 'true' || val === 1 ? true : null

      // Step 2 – check leaveTypeOptions (from /api/leave-types); trust explicit `true` only
      const selectedOption = this.leaveTypeOptions.find(
        opt => opt.value === this.leaveForm.leave_type || opt.text === this.leaveForm.leave_type,
      )
      if (selectedOption && isExplicitlyEnabled(selectedOption.canBeHalfDay) === true) return true

      // Step 3 – check leaveTypesFromDB; trust explicit `true` only
      const foundType = this.leaveTypesFromDB.find(
        lt => lt.code === this.leaveForm.leave_type || lt.display_name === this.leaveForm.leave_type,
      )
      if (foundType && isExplicitlyEnabled(foundType.can_be_half_day) === true) return true

      // Step 4 – default: all non-excluded types support half-day
      return true
    },
    displayedLeaves() {
      // In Team View or Org View, show all team leaves; in Employee View, show only own leaves
      let leavesToDisplay
      // Check if showing all team data (no specific selection)
      const isShowingAllTeam = (this.viewMode === 'team' || this.viewMode === 'org') && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
      
      if (isShowingAllTeam) {
        leavesToDisplay = this.allTeamLeaves
        
        // Filter by Account Manager's team when Account Manager is selected (for HR/HR Director/Admin/Master)
        if (this.selectedAccountManager && this.canApplyOnBehalfAll && this.teamWiseEmployees && this.teamWiseEmployees.length > 0) {
          const teamMemberIds = this.teamWiseEmployees.map(emp => emp.emp_id || emp.id).filter(id => id)
          if (teamMemberIds.length > 0) {
            leavesToDisplay = leavesToDisplay.filter(leave => teamMemberIds.includes(leave.emp_id))
          }
        }
        
        // Apply filter if set
        if (this.teamLeaveFilter !== 'all') {
          leavesToDisplay = leavesToDisplay.filter(leave => {
            const status = (leave.status || '').toLowerCase()
            if (this.teamLeaveFilter === 'pending') {
              return status.includes('pending') || status.includes('manager approved - pending hr')
            } else if (this.teamLeaveFilter === 'approved') {
              return status === 'approved'
            } else if (this.teamLeaveFilter === 'rejected') {
              return status === 'rejected'
            }
            return true
          })
        }
      } else {
        // In Employee View or when specific team member/employee is selected, show only their leaves
        if ((this.viewMode === 'team' || this.viewMode === 'org') && this.selectedTeamMember) {
          // Account Manager selected a specific team member
          leavesToDisplay = this.myLeaves.filter(leave => leave.emp_id === this.selectedTeamMember)
        } else if ((this.viewMode === 'team' || this.viewMode === 'org') && this.selectedTeamWiseEmployee) {
          // HR/HR-DIRECTOR selected a specific employee
          leavesToDisplay = this.myLeaves.filter(leave => leave.emp_id === this.selectedTeamWiseEmployee)
        } else {
          // In Employee View, ensure only own leaves are shown (filter by emp_id)
          leavesToDisplay = this.myLeaves.filter(leave => leave.emp_id === this.emp_id)
        }
        
        // Apply filter if set (for employee view and selected team members)
        if (this.teamLeaveFilter !== 'all') {
          leavesToDisplay = leavesToDisplay.filter(leave => {
            const status = (leave.status || '').toLowerCase()
            if (this.teamLeaveFilter === 'pending') {
              return status.includes('pending') || status.includes('manager approved - pending hr')
            } else if (this.teamLeaveFilter === 'approved') {
              return status === 'approved'
            } else if (this.teamLeaveFilter === 'rejected') {
              return status === 'rejected'
            }
            return true
          })
        }
      }
      
      // Apply date range filter if set
      if (this.dateRangeFilter && Array.isArray(this.dateRangeFilter) && this.dateRangeFilter.length === 2) {
        const startDate = moment(this.dateRangeFilter[0])
        const endDate = moment(this.dateRangeFilter[1])
        leavesToDisplay = leavesToDisplay.filter(leave => {
          const leaveFromDate = moment(leave.from_date)
          const leaveToDate = moment(leave.to_date || leave.from_date)
          // Check if leave overlaps with date range
          return (leaveFromDate.isSameOrAfter(startDate, 'day') && leaveFromDate.isSameOrBefore(endDate, 'day')) ||
                 (leaveToDate.isSameOrAfter(startDate, 'day') && leaveToDate.isSameOrBefore(endDate, 'day')) ||
                 (leaveFromDate.isBefore(startDate, 'day') && leaveToDate.isAfter(endDate, 'day'))
        })
      }
      
      return leavesToDisplay.slice(0, this.displayedLeavesLimit)
    },
    totalLeavesCount() {
      // Calculate total leaves count (same logic as displayedLeaves but without slice)
      let leavesToCount
      // Check if showing all team data (no specific selection)
      const isShowingAllTeam = this.viewMode === 'team' && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
      
      if (isShowingAllTeam) {
        leavesToCount = this.allTeamLeaves
        
        // Filter by Account Manager's team when Account Manager is selected (for HR/HR Director/Admin/Master)
        if (this.selectedAccountManager && this.canApplyOnBehalfAll && this.teamWiseEmployees && this.teamWiseEmployees.length > 0) {
          const teamMemberIds = this.teamWiseEmployees.map(emp => emp.emp_id || emp.id).filter(id => id)
          if (teamMemberIds.length > 0) {
            leavesToCount = leavesToCount.filter(leave => teamMemberIds.includes(leave.emp_id))
          }
        }
        
        // Apply filter if set
        if (this.teamLeaveFilter !== 'all') {
          leavesToCount = leavesToCount.filter(leave => {
            const status = (leave.status || '').toLowerCase()
            if (this.teamLeaveFilter === 'pending') {
              return status.includes('pending') || status.includes('manager approved - pending hr')
            } else if (this.teamLeaveFilter === 'approved') {
              return status === 'approved'
            } else if (this.teamLeaveFilter === 'rejected') {
              return status === 'rejected'
            }
            return true
          })
        }
      } else {
        // In Employee View or when specific team member/employee is selected, show only their leaves
        if (this.viewMode === 'team' && this.selectedTeamMember) {
          // Account Manager selected a specific team member
          leavesToCount = this.myLeaves.filter(leave => leave.emp_id === this.selectedTeamMember)
        } else if (this.viewMode === 'team' && this.selectedTeamWiseEmployee) {
          // HR/HR-DIRECTOR selected a specific employee
          leavesToCount = this.myLeaves.filter(leave => leave.emp_id === this.selectedTeamWiseEmployee)
        } else {
          // In Employee View, ensure only own leaves are shown (filter by emp_id)
          leavesToCount = this.myLeaves.filter(leave => leave.emp_id === this.emp_id)
        }
        
        // Apply filter if set (for employee view and selected team members)
        if (this.teamLeaveFilter !== 'all') {
          leavesToCount = leavesToCount.filter(leave => {
            const status = (leave.status || '').toLowerCase()
            if (this.teamLeaveFilter === 'pending') {
              return status.includes('pending') || status.includes('manager approved - pending hr')
            } else if (this.teamLeaveFilter === 'approved') {
              return status === 'approved'
            } else if (this.teamLeaveFilter === 'rejected') {
              return status === 'rejected'
            }
            return true
          })
        }
      }
      
      return leavesToCount.length
    },
    pendingHRApprovals() {
      const isShowingAllTeam = (this.viewMode === 'team' || this.viewMode === 'org') && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
      if (!isShowingAllTeam) return []
      let leavesToFilter = this.allTeamLeaves
      
      // Filter by Account Manager's team when Account Manager is selected (for HR/HR Director/Admin/Master)
      if (this.selectedAccountManager && this.canApplyOnBehalfAll && this.teamWiseEmployees && this.teamWiseEmployees.length > 0) {
        const teamMemberIds = this.teamWiseEmployees.map(emp => emp.emp_id || emp.id).filter(id => id)
        if (teamMemberIds.length > 0) {
          leavesToFilter = leavesToFilter.filter(leave => teamMemberIds.includes(leave.emp_id))
        }
      }
      
      const filtered = leavesToFilter.filter(leave => {
        const status = (leave.status || '').toLowerCase()
        // For HR/HR Director: show any pending status that needs HR approval
        // This includes: pending, pending hr, manager approved - pending hr, etc.
        return status.includes('pending') || 
               status.includes('manager approved')
      })
      return filtered.slice(0, 10) // Limit to 10 for quick view
    },
    pendingRegularizationsForApproval() {
      // Show pending regularizations the current user can Approve/Reject (team/org)
      const isShowingAllTeam = (this.viewMode === 'team' || this.viewMode === 'org') && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
      if (!isShowingAllTeam || !this.canManageLeaves) return []

      const fromPendingList = Array.isArray(this.pendingRegularizations) ? this.pendingRegularizations : []
      const fromCases = Array.isArray(this.regularizationCases) ? this.regularizationCases : []
      let regsToFilter = fromPendingList.length > 0 ? fromPendingList : fromCases

      const amTeamIds = this.accountManagerTeamMemberIds
      if (amTeamIds && amTeamIds.length > 0) {
        const idSet = new Set(amTeamIds.map(id => String(id)))
        regsToFilter = regsToFilter.filter(reg => reg && reg.emp_id && idSet.has(String(reg.emp_id)))
      }

      const todayLocal = moment().format('YYYY-MM-DD')
      return regsToFilter.filter(reg => {
        if (!this.isRegularizationActionableForCurrentUser(reg)) return false
        const caseDate = reg.datestring || reg.date
        if (caseDate && moment(caseDate).format('YYYY-MM-DD') === todayLocal) return false
        return true
      })
    },
    /**
     * Predicate: leave row would show Approve/Reject for the current user.
     * Mirrors LeavesRegularizationTabs.showLeaveApprovalFor (without canManageLeaves gate).
     */
    isLeaveActionableForCurrentUser() {
      return (leave) => {
        if (!leave) return false
        if (typeof this.needsLeaveApproval === 'function' && !this.needsLeaveApproval(leave)) return false
        if (typeof this.canApproveLeave === 'function' && this.canApproveLeave(leave)) return true
        if (this.canApproveLeaveAllStages) return true
        if (this.viewMode !== 'team' && this.viewMode !== 'org') return false
        const isManager =
          typeof this.isManagerOfEmployee === 'function' && this.isManagerOfEmployee(leave.emp_id)
        const pendingManager =
          typeof this.isLeavePendingManagerApproval === 'function' &&
          this.isLeavePendingManagerApproval(leave)
        return isManager && pendingManager
      }
    },

    /**
     * Predicate: regularization row would show Approve/Reject for the current user.
     * Mirrors LeavesRegularizationTabs.showRegApprovalFor (without canManageLeaves gate).
     */
    isRegularizationActionableForCurrentUser() {
      return (reg) => {
        if (!reg) return false
        const raised =
          typeof this.isRegularizationRaised === 'function'
            ? this.isRegularizationRaised(reg)
            : !!(reg.status || reg.regularization_status)
        if (!raised) return false
        if (this.canApproveRegularizationAllStages) return true
        if (typeof this.canApproveItem === 'function' && this.canApproveItem(reg, 'regularization')) {
          return true
        }
        if (this.viewMode !== 'team' && this.viewMode !== 'org') return false
        const isManager =
          typeof this.isManagerOfEmployee === 'function' && this.isManagerOfEmployee(reg.emp_id)
        const pendingManager =
          typeof this.needsManagerApproval === 'function' && this.needsManagerApproval(reg)
        return isManager && pendingManager
      }
    },

    approvalPendingCount() {
      // Team/org: count leave/WFH/CO + regularization items the current user can Approve/Reject.
      // Employee / filtered-employee: count that employee's items still awaiting any approval.
      const selectedEmpId = this.selectedTeamMember || this.selectedTeamWiseEmployee
      const isFilteredEmployeeView = (this.viewMode === 'team' || this.viewMode === 'org') && selectedEmpId
      const isTeamView = !isFilteredEmployeeView && (this.viewMode === 'team' || this.viewMode === 'org') && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
      const targetEmpId = isFilteredEmployeeView ? selectedEmpId : (this.currentViewEmpId || this.emp_id)

      let leavesToCheck
      if (isFilteredEmployeeView) {
        leavesToCheck = (this.myLeaves || []).filter(leave =>
          leave && (!leave.emp_id || String(leave.emp_id) === String(selectedEmpId))
        )
      } else if (isTeamView) {
        leavesToCheck = Array.isArray(this.allTeamLeaves) ? this.allTeamLeaves.flat().filter(Boolean) : []
      } else {
        leavesToCheck = (this.myLeaves || []).filter(leave =>
          leave && (!leave.emp_id || String(leave.emp_id) === String(targetEmpId))
        )
      }

      // Filter by Account Manager's team when Account Manager is selected (string-safe)
      const amTeamIds = this.accountManagerTeamMemberIds
      if (isTeamView && amTeamIds && amTeamIds.length > 0) {
        const idSet = new Set(amTeamIds.map(id => String(id)))
        leavesToCheck = leavesToCheck.filter(leave => leave && idSet.has(String(leave.emp_id)))
      }

      let pendingLeavesCount = 0
      if (isTeamView) {
        pendingLeavesCount = leavesToCheck.filter(leave => this.isLeaveActionableForCurrentUser(leave)).length
      } else {
        pendingLeavesCount = leavesToCheck.filter(leave => {
          const status = (leave.status || '').trim().toLowerCase()
          if (!status || status === 'approved' || status === 'rejected' || status === 'cancelled' ||
              status === 'hr reject' || status.includes('reject') || status.includes('cancel')) {
            return false
          }
          return status.includes('pending') || status === 'pending'
        }).length
      }

      let pendingRegularizationsCount = 0
      if (isTeamView) {
        // Prefer dedicated pending-approvals list, then fall back to raised cases the user can act on
        const fromPendingList = Array.isArray(this.pendingRegularizations) ? this.pendingRegularizations : []
        const fromCases = Array.isArray(this.regularizationCases) ? this.regularizationCases : []
        let regsToCheck = fromPendingList.length > 0 ? fromPendingList : fromCases
        if (amTeamIds && amTeamIds.length > 0) {
          const idSet = new Set(amTeamIds.map(id => String(id)))
          regsToCheck = regsToCheck.filter(reg => reg && reg.emp_id && idSet.has(String(reg.emp_id)))
        }
        const todayLocal = moment().format('YYYY-MM-DD')
        pendingRegularizationsCount = regsToCheck.filter(reg => {
          if (!this.isRegularizationActionableForCurrentUser(reg)) return false
          const caseDate = reg.datestring || reg.date
          if (caseDate && moment(caseDate).format('YYYY-MM-DD') === todayLocal) return false
          return true
        }).length
      } else if (this.regularizationCases && Array.isArray(this.regularizationCases)) {
        let regsToCheck = this.regularizationCases
        if (isFilteredEmployeeView) {
          regsToCheck = regsToCheck.filter(reg => reg && String(reg.emp_id) === String(selectedEmpId))
        } else {
          regsToCheck = regsToCheck.filter(reg => reg && (!reg.emp_id || String(reg.emp_id) === String(targetEmpId)))
        }
        const todayLocal = moment().format('YYYY-MM-DD')
        pendingRegularizationsCount = regsToCheck.filter(reg => {
          const caseDate = reg.datestring || reg.date
          if (caseDate && moment(caseDate).format('YYYY-MM-DD') === todayLocal) return false
          const status = (reg.status || reg.regularization_status || '').trim().toLowerCase()
          if (!status || status === 'approved' || status === 'rejected' || status === 'cancelled' ||
              status === 'hr reject' || status.includes('reject')) {
            return false
          }
          // Raised regs only (plain "pending" = not yet submitted)
          return status === 'pending manager' ||
                 status === 'pending hr' ||
                 status === 'pending manager approval' ||
                 status === 'pending hr approval' ||
                 status === 'manager approved - pending hr' ||
                 status.includes('pending manager') ||
                 status.includes('pending hr') ||
                 (status.includes('pending') && status !== 'pending')
        }).length
      }

      return pendingLeavesCount + pendingRegularizationsCount
    },
    upcomingTeamLeaves() {
      if (this.viewMode !== 'team' && this.viewMode !== 'org') return []
      
      const isShowingAllTeam = (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
      
      const filterByEmpId = this.selectedTeamMember || this.selectedTeamWiseEmployee
      
      // Determine which leaves to check
      let leavesToCheck = isShowingAllTeam 
        ? (Array.isArray(this.allTeamLeaves) ? this.allTeamLeaves.flat() : [])
        : this.myLeaves.filter(leave => {
            return leave && filterByEmpId && String(leave.emp_id) === String(filterByEmpId)
          })

      const amTeamIds = this.accountManagerTeamMemberIds
      if (isShowingAllTeam && amTeamIds && amTeamIds.length > 0) {
        const idSet = new Set(amTeamIds.map(id => String(id)))
        leavesToCheck = leavesToCheck.filter(leave => leave && leave.emp_id && idSet.has(String(leave.emp_id)))
      }
      
      const today = moment().startOf('day')
      const filtered = leavesToCheck.filter(leave => {
        if (!leave || !leave.from_date) return false
        if (!this.isApprovedLeaveForOut(leave.status)) return false
        try {
          const fromDate = this.parseTeamLeaveDay(leave.from_date)
          if (!fromDate) return false
          // Show leaves that are today or in the future
          const isUpcoming = fromDate.isAfter(today, 'day') || fromDate.isSame(today, 'day')
          return isUpcoming
        } catch (e) {
          return false
        }
      })
      return filtered
    },

    /**
     * Leave requests the current user can act on (Team Leader action queue).
     * Matches Leaves tab pending filter: actionable for current user (includes WFH).
     */
    pendingLeaveApprovalsForQueue() {
      const isShowingAllTeam = (this.viewMode === 'team' || this.viewMode === 'org') && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
      if (!isShowingAllTeam || !this.canManageLeaves) return []

      let leavesToFilter = Array.isArray(this.allTeamLeaves) ? this.allTeamLeaves.flat().filter(Boolean) : []
      const amTeamIds = this.accountManagerTeamMemberIds
      if (amTeamIds && amTeamIds.length > 0) {
        const idSet = new Set(amTeamIds.map(id => String(id)))
        leavesToFilter = leavesToFilter.filter(leave => leave && leave.emp_id && idSet.has(String(leave.emp_id)))
      }

      // Same set as Leaves tab with Pending filter (actionable, includes WFH)
      return leavesToFilter.filter(leave => this.isLeaveActionableForCurrentUser(leave))
    },

    /**
     * Parse leave date to local start-of-day (handles ISO + common DD/MM forms).
     */
    parseTeamLeaveDay() {
      return parseAttendanceLeaveDay
    },

    /**
     * Approved (or manager/OH-approved awaiting HR) leave/WFH — used for Out this week.
     */
    isApprovedLeaveForOut() {
      return isApprovedLeaveStatus
    },

    /**
     * Pending (not yet approved) leave/WFH overlapping current ISO week.
     */
    teamPendingOutThisWeekCount() {
      if (this.viewMode !== 'team' && this.viewMode !== 'org') return 0
      const bounds = this.teamCurrentWeekBounds
      if (!bounds) return 0

      let leavesToCheck = Array.isArray(this.allTeamLeaves) ? this.allTeamLeaves.flat().filter(Boolean) : []
      const amTeamIds = this.accountManagerTeamMemberIds
      if (amTeamIds && amTeamIds.length > 0) {
        const idSet = new Set(amTeamIds.map(id => String(id)))
        leavesToCheck = leavesToCheck.filter(leave => leave && leave.emp_id && idSet.has(String(leave.emp_id)))
      }

      const parseDay = this.parseTeamLeaveDay
      const { weekStart, weekEnd, today } = bounds
      const ids = new Set()
      leavesToCheck.forEach(leave => {
        if (!leave || !leave.from_date || !leave.emp_id) return
        const status = String(leave.status || '').trim().toLowerCase()
        if (!status || status === 'approved' || status.includes('reject') || status.includes('cancel')) return
        if (!(status.includes('pending') || status === 'pending')) return
        // Exclude manager-approved pipeline (those count as "out")
        if (this.isApprovedLeaveForOut(leave.status)) return
        const fromDate = parseDay(leave.from_date)
        const toDate = parseDay(leave.to_date || leave.from_date)
        if (!fromDate || !toDate) return
        const overlapsWeek = fromDate.isSameOrBefore(weekEnd, 'day') && toDate.isSameOrAfter(weekStart, 'day')
        const stillRelevant = toDate.isSameOrAfter(today, 'day')
        if (overlapsWeek && stillRelevant) ids.add(String(leave.emp_id))
      })
      return ids.size
    },

    /**
     * Current ISO week (Mon–Sun) bounds in local time.
     */
    teamCurrentWeekBounds() {
      const today = moment().startOf('day')
      const weekStart = moment().startOf('isoWeek').startOf('day')
      const weekEnd = moment().endOf('isoWeek').startOf('day')
      return { today, weekStart, weekEnd }
    },

    /**
     * Approved team leaves/WFH overlapping current ISO week (Mon–Sun), still relevant today+.
     * Does not depend on punch maps.
     */
    teamOutThisWeek() {
      if (this.viewMode !== 'team' && this.viewMode !== 'org') return []

      const isShowingAllTeam = (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
      if (!isShowingAllTeam) return []

      let leavesToCheck = Array.isArray(this.allTeamLeaves) ? this.allTeamLeaves.flat().filter(Boolean) : []
      const amTeamIds = this.accountManagerTeamMemberIds
      if (amTeamIds && amTeamIds.length > 0) {
        const idSet = new Set(amTeamIds.map(id => String(id)))
        leavesToCheck = leavesToCheck.filter(leave => leave && leave.emp_id && idSet.has(String(leave.emp_id)))
      }

      const { today, weekStart, weekEnd } = this.teamCurrentWeekBounds
      const parseDay = this.parseTeamLeaveDay

      const overlapping = leavesToCheck.filter(leave => {
        if (!leave || !leave.from_date) return false
        if (!this.isApprovedLeaveForOut(leave.status)) return false
        const fromDate = parseDay(leave.from_date)
        const toDate = parseDay(leave.to_date || leave.from_date)
        if (!fromDate || !toDate) return false
        // Overlaps Mon–Sun week and has not already ended before today
        const overlapsWeek = fromDate.isSameOrBefore(weekEnd, 'day') && toDate.isSameOrAfter(weekStart, 'day')
        const stillRelevant = toDate.isSameOrAfter(today, 'day')
        return overlapsWeek && stillRelevant
      })

      return overlapping
        .map(leave => {
          const fromDate = parseDay(leave.from_date)
          const toDate = parseDay(leave.to_date || leave.from_date)
          const isToday = fromDate && toDate
            ? today.isSameOrAfter(fromDate, 'day') && today.isSameOrBefore(toDate, 'day')
            : false
          const isWfh = typeof this.isWFHLeave === 'function'
            ? this.isWFHLeave(leave)
            : (() => {
              const leaveType = (leave.leave_type || leave.type || '').toLowerCase()
              return leaveType.includes('work from home') || leaveType === 'wfh'
            })()
          return {
            ...leave,
            isToday,
            isWfh,
            sortKey: (isToday ? '0' : '1') + (fromDate ? fromDate.format('YYYYMMDD') : '') + String(leave.emp_id || ''),
          }
        })
        .sort((a, b) => {
          if (a.isToday !== b.isToday) return a.isToday ? -1 : 1
          return a.sortKey.localeCompare(b.sortKey)
        })
    },

    /**
     * Approved leave/WFH covering today (Team View Out today card).
     * Distinct from pending queues and punch-based Today's attendance.
     */
    teamOutToday() {
      const parseDay = this.parseTeamLeaveDay
      const items = (this.teamOutThisWeek || []).filter(item => item && item.isToday)
      return items
        .map(leave => {
          const fromDate = parseDay(leave.from_date)
          const toDate = parseDay(leave.to_date || leave.from_date)
          const isMultiDay = !!(fromDate && toDate && !fromDate.isSame(toDate, 'day'))
          return {
            ...leave,
            typeBadge: this.shortLeaveTypeBadge(leave),
            dateRangeLabel: isMultiDay
              ? `${fromDate.format('DD MMM')} – ${toDate.format('DD MMM')}`
              : '',
            sortKey: String(leave.emp_id || '') + (fromDate ? fromDate.format('YYYYMMDD') : ''),
          }
        })
        .sort((a, b) => {
          const nameA = (typeof this.getUserName === 'function' ? this.getUserName(a.emp_id) : '') || ''
          const nameB = (typeof this.getUserName === 'function' ? this.getUserName(b.emp_id) : '') || ''
          if (nameA !== nameB) return nameA.localeCompare(nameB)
          return (a.sortKey || '').localeCompare(b.sortKey || '')
        })
    },

    /** Short leave/WFH badge for dense Out today rows (PL/CL/SL/WFH/CO/…). */
    shortLeaveTypeBadge() {
      return (leave) => {
        if (!leave) return 'Leave'
        const isWfh = leave.isWfh != null
          ? leave.isWfh
          : (typeof this.isWFHLeave === 'function'
            ? this.isWFHLeave(leave)
            : (() => {
              const t = (leave.leave_type || leave.type || '').toLowerCase()
              return t.includes('work from home') || t === 'wfh'
            })())
        if (isWfh) return 'WFH'
        const raw = String(leave.leave_type || leave.type || '').trim()
        if (!raw) return 'Leave'
        const lower = raw.toLowerCase()
        if (lower === 'pl' || lower.includes('privilege') || lower === 'pto') return 'PL'
        if (lower === 'cl' || lower.includes('casual')) return 'CL'
        if (lower === 'sl' || lower.includes('sick')) return 'SL'
        if (lower === 'co' || lower.includes('comp')) return 'CO'
        if (lower.includes('maternity')) return 'ML'
        if (lower.includes('paternity')) return 'PT'
        if (raw.length <= 4) return raw.toUpperCase()
        return raw
          .split(/\s+/)
          .map(w => w.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 4) || 'Leave'
      }
    },

    teamOutTodayCount() {
      const items = this.teamOutToday || []
      const todayIds = new Set(
        items.filter(item => item && item.emp_id).map(item => String(item.emp_id))
      )
      return todayIds.size
    },

    teamOutThisWeekCount() {
      const items = this.teamOutThisWeek || []
      const ids = new Set(items.filter(item => item && item.emp_id).map(item => String(item.emp_id)))
      return ids.size
    },

    /**
     * Scoped team member IDs for Team View today KPIs (AM filter aware).
     */
    teamKpiMemberIds() {
      const amTeamIds = this.accountManagerTeamMemberIds
      if (amTeamIds && amTeamIds.length > 0) {
        return amTeamIds.map(id => String(id))
      }
      const fromMembers = (this.teamMembers || [])
        .map(m => resolveMemberEmpId(m))
        .filter(Boolean)
        .map(id => String(id))
      if (fromMembers.length > 0) return fromMembers

      // Org dashboard rows sometimes omit emp_id — derive scope from loaded leave records
      const fromLeaves = new Set()
      ;(Array.isArray(this.allTeamLeaves) ? this.allTeamLeaves.flat() : []).forEach(leave => {
        const id = resolveLeaveEmpId(leave)
        if (id) fromLeaves.add(String(id))
      })
      return [...fromLeaves]
    },

    /**
     * Team View today attendance buckets — prefer team_summary API fields when present,
     * else derive from leaves + today's punch map (aligned with calendar statuses).
     * Returns: { present, absent, wfh }
     */
    teamTodayAttendanceKpis() {
      const apiSummary = this.teamDashboardData?.team_summary || this.teamDashboardData?.org_summary || {}
      const apiPresent = apiSummary.present_today ?? apiSummary.present_count
      const apiAbsent = apiSummary.absent_today ?? apiSummary.absent_count
      const apiWfh = apiSummary.wfh_today ?? apiSummary.wfh_today_count

      const memberIds = this.teamKpiMemberIds || []
      const memberIdSet = new Set(memberIds)
      const today = moment().startOf('day')
      const todayStr = today.format('YYYY-MM-DD')
      const isWeekend = today.day() === 0 || today.day() === 6
      const isHoliday = typeof this.isHolidayDate === 'function' && this.isHolidayDate(todayStr)
      const isCompanyWfh = typeof this.isCompanyWfhDate === 'function' && this.isCompanyWfhDate(todayStr)

      const parseDay = this.parseTeamLeaveDay
      const flatLeaves = Array.isArray(this.allTeamLeaves) ? this.allTeamLeaves.flat().filter(Boolean) : []
      const inScope = (leave) => {
        const empId = resolveLeaveEmpId(leave)
        if (!empId) return false
        if (memberIdSet.size > 0 && !memberIdSet.has(String(empId))) return false
        return true
      }

      // Approved leaves covering today (scoped) — same status rules as Out this week
      const leavesToday = flatLeaves.filter(leave => {
        if (!inScope(leave)) return false
        if (!isApprovedLeaveStatus(leave.status)) return false
        return leaveCoversDay(leave, today, parseDay)
      })

      const personalWfhIds = new Set()
      const onLeaveNonWfhIds = new Set()
      leavesToday.forEach(leave => {
        const empId = String(resolveLeaveEmpId(leave))
        const isWfh = typeof this.isWFHLeave === 'function'
          ? this.isWFHLeave(leave)
          : isWFHLeaveRecord(leave)
        if (isWfh) personalWfhIds.add(empId)
        else onLeaveNonWfhIds.add(empId)
      })

      // WFH Today: personal approved WFH ∪ company-WFH day members not on non-WFH leave
      const wfhIds = new Set(personalWfhIds)
      if (isCompanyWfh) {
        memberIds.forEach(id => {
          if (!onLeaveNonWfhIds.has(id)) wfhIds.add(id)
        })
      }
      // Shared counter — also matches single-day WFH rows that use `date` instead of from/to
      const derivedFromHelper = countApprovedWfhToday(flatLeaves, {
        memberIdSet: memberIdSet,
        today,
        parseDay,
      })
      const derivedWfh = Math.max(wfhIds.size, derivedFromHelper)

      // Punch map for today
      const punchByEmp = new Map()
      ;(this.teamTodayPunchRecords || []).forEach(rec => {
        if (!rec || !rec.emp_id) return
        const empId = String(rec.emp_id)
        if (!memberIdSet.has(empId)) return
        const recDate = rec.date || rec.datestring
        if (recDate && moment(recDate).format('YYYY-MM-DD') !== todayStr) return
        const inDetails = rec.punch_details?.punch_in_details || rec.punch_details?.punchInDetails || {}
        const hasIn = !!(
          rec.intime ||
          rec.punch_in_time ||
          rec.punchInTime ||
          rec.punch_in ||
          rec.in_time ||
          inDetails.punch_in_time ||
          inDetails.punchInTime ||
          inDetails.time
        )
        const attendance = (rec.attendance || '').toLowerCase()
        const isPresentLike =
          hasIn ||
          attendance === 'present' ||
          attendance === 'half-day' ||
          attendance === 'half_day'
        if (isPresentLike) punchByEmp.set(empId, true)
      })

      let derivedPresent = 0
      let derivedAbsent = 0
      if (!isWeekend && !isHoliday) {
        memberIds.forEach(empId => {
          if (onLeaveNonWfhIds.has(empId)) return
          // Approved WFH counted only in WFH KPI (matches Today's attendance WFH tab)
          if (wfhIds.has(empId)) return
          if (punchByEmp.get(empId)) {
            derivedPresent += 1
          } else {
            derivedAbsent += 1
          }
        })
      }

      const apiPresentNum = apiPresent != null && apiPresent !== '' ? Number(apiPresent) || 0 : null
      const apiAbsentNum = apiAbsent != null && apiAbsent !== '' ? Number(apiAbsent) || 0 : null
      const apiWfhNum = apiWfh != null && apiWfh !== '' ? Number(apiWfh) || 0 : null

      return {
        present: apiPresentNum != null ? apiPresentNum : derivedPresent,
        absent: apiAbsentNum != null ? apiAbsentNum : derivedAbsent,
        // API wfh_today is often 0/stale — never under-count vs loaded leave records.
        // Counts APPROVED WFH (incl. manager-approved pending HR), not pending requests.
        wfh: derivedWfh > 0 ? Math.max(apiWfhNum || 0, derivedWfh) : (apiWfhNum != null ? apiWfhNum : derivedWfh),
      }
    },

    teamPresentTodayCount() {
      return (this.teamTodayAttendanceKpis && this.teamTodayAttendanceKpis.present) || 0
    },

    teamAbsentTodayCount() {
      return (this.teamTodayAttendanceKpis && this.teamTodayAttendanceKpis.absent) || 0
    },

    teamWfhTodayCount() {
      return (this.teamTodayAttendanceKpis && this.teamTodayAttendanceKpis.wfh) || 0
    },

    /**
     * Named roster for Team View "Today's attendance" panel.
     * Uses same leave + punch rules as teamTodayAttendanceKpis (not API summary counts).
     */
    teamTodayAttendanceRoster() {
      const empty = {
        present: [],
        absent: [],
        wfh: [],
        onLeaveCount: 0,
        onLeaveEmpIds: [],
        isNonWorkingDay: false,
        nonWorkingLabel: 'Non-working day',
      }
      if (this.viewMode !== 'team' && this.viewMode !== 'org') return empty

      const memberIds = this.teamKpiMemberIds || []
      if (memberIds.length === 0) return empty

      const memberIdSet = new Set(memberIds)
      const today = moment().startOf('day')
      const todayStr = today.format('YYYY-MM-DD')
      const isWeekend = today.day() === 0 || today.day() === 6
      const isHoliday = typeof this.isHolidayDate === 'function' && this.isHolidayDate(todayStr)
      const isCompanyWfh = typeof this.isCompanyWfhDate === 'function' && this.isCompanyWfhDate(todayStr)

      if (isWeekend || isHoliday) {
        return {
          ...empty,
          isNonWorkingDay: true,
          nonWorkingLabel: isHoliday ? 'Holiday today' : 'Weekend — no punch expected',
        }
      }

      const parseDay = this.parseTeamLeaveDay
      const flatLeaves = Array.isArray(this.allTeamLeaves) ? this.allTeamLeaves.flat().filter(Boolean) : []
      const inScope = (leave) => {
        const empId = resolveLeaveEmpId(leave)
        if (!empId) return false
        if (memberIdSet.size > 0 && !memberIdSet.has(String(empId))) return false
        return true
      }

      const leavesToday = flatLeaves.filter(leave => {
        if (!inScope(leave)) return false
        if (!isApprovedLeaveStatus(leave.status)) return false
        return leaveCoversDay(leave, today, parseDay)
      })

      const personalWfhIds = new Set()
      const onLeaveNonWfhIds = new Set()
      leavesToday.forEach(leave => {
        const empId = String(resolveLeaveEmpId(leave))
        const isWfh = typeof this.isWFHLeave === 'function'
          ? this.isWFHLeave(leave)
          : isWFHLeaveRecord(leave)
        if (isWfh) personalWfhIds.add(empId)
        else onLeaveNonWfhIds.add(empId)
      })

      const wfhIds = new Set(personalWfhIds)
      if (isCompanyWfh) {
        memberIds.forEach(id => {
          if (!onLeaveNonWfhIds.has(id)) wfhIds.add(id)
        })
      }

      const formatCompactPunchTime = (raw) => {
        if (raw == null || raw === '') return null
        const str = String(raw).trim().replace('T,', 'T')
        if (!str || str === '0:00:00' || str === '00:00:00' || str === '0:00' || str === '00:00') return null
        if (str.includes('T') || /\d{4}-\d{2}-\d{2}/.test(str)) {
          const parsed = moment(str)
          if (parsed.isValid()) return parsed.format('HH:mm')
        }
        const match = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/)
        if (match) return `${match[1].padStart(2, '0')}:${match[2]}`
        const parsed = moment(str, ['HH:mm:ss', 'HH:mm', 'H:mm:ss', 'H:mm', 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'], true)
        return parsed.isValid() ? parsed.format('HH:mm') : null
      }

      const pickPunchInRaw = (rec) => {
        if (!rec) return null
        const details = rec.punch_details || {}
        const inDetails = details.punch_in_details || details.punchInDetails || {}
        return (
          rec.intime ||
          rec.punch_in_time ||
          rec.punchInTime ||
          rec.punch_in ||
          rec.in_time ||
          rec.att_in_time ||
          inDetails.punch_in_time ||
          inDetails.punchInTime ||
          inDetails.time ||
          inDetails.in_time ||
          inDetails.intime ||
          details.punch_in_time ||
          null
        )
      }

      const pickPunchOutRaw = (rec) => {
        if (!rec) return null
        const details = rec.punch_details || {}
        const outDetails = details.punch_out_details || details.punchOutDetails || {}
        return (
          rec.outtime ||
          rec.punch_out_time ||
          rec.punchOutTime ||
          rec.punch_out ||
          rec.out_time ||
          rec.att_out_time ||
          outDetails.punch_out_time ||
          outDetails.punchOutTime ||
          outDetails.time ||
          outDetails.out_time ||
          outDetails.outtime ||
          details.punch_out_time ||
          null
        )
      }

      // empId -> { present, punchIn, punchOut }
      const punchByEmp = new Map()
      ;(this.teamTodayPunchRecords || []).forEach(rec => {
        if (!rec || !rec.emp_id) return
        const empId = String(rec.emp_id)
        if (!memberIdSet.has(empId)) return
        const recDate = rec.date || rec.datestring
        if (recDate && moment(recDate).format('YYYY-MM-DD') !== todayStr) return
        const punchIn = formatCompactPunchTime(pickPunchInRaw(rec))
        const punchOut = formatCompactPunchTime(pickPunchOutRaw(rec))
        const hasIn = !!(punchIn || pickPunchInRaw(rec))
        const attendance = (rec.attendance || '').toLowerCase()
        const isPresentLike =
          hasIn ||
          attendance === 'present' ||
          attendance === 'half-day' ||
          attendance === 'half_day'
        const prev = punchByEmp.get(empId)
        // Prefer earliest in / latest out when multiple records exist for the day
        const nextIn = punchIn && prev && prev.punchIn
          ? (punchIn < prev.punchIn ? punchIn : prev.punchIn)
          : ((prev && prev.punchIn) || punchIn)
        const nextOut = punchOut && prev && prev.punchOut
          ? (punchOut > prev.punchOut ? punchOut : prev.punchOut)
          : (punchOut || (prev && prev.punchOut) || null)
        punchByEmp.set(empId, {
          present: !!(prev && prev.present) || isPresentLike,
          punchIn: nextIn || null,
          punchOut: nextOut,
        })
      })

      // Optional per-employee shift fields (none found in current APIs; pass through if present)
      // so Late IN / Early Out can use start+15 / end−15 instead of company defaults.
      const memberOf = (empId) => {
        const sources = [
          ...(this.teamMembers || []),
          ...(this.teamWiseEmployees || []),
        ]
        return sources.find(m => m && String(m.emp_id || m.id) === String(empId)) || null
      }

      const nameOf = (empId) => {
        if (typeof this.getUserName === 'function') {
          const n = this.getUserName(empId)
          if (n) return n
        }
        const member = memberOf(empId)
        if (member) {
          return member.name || member.emp_name || member.full_name ||
            [member.first_name, member.last_name].filter(Boolean).join(' ') ||
            String(empId)
        }
        return String(empId)
      }

      const present = []
      const absent = []
      // Dedicated WFH tab: approved WFH today (personal or company-wide) — exclusive, not also In/Not in
      const wfh = []

      memberIds.forEach(empId => {
        // Approved non-WFH leave — exclude from present/absent/wfh roster
        if (onLeaveNonWfhIds.has(empId)) return

        const punch = punchByEmp.get(empId)
        const onApprovedWfh = wfhIds.has(empId)
        const shiftFields = extractShiftFieldsFromMember(memberOf(empId))
        const row = {
          empId,
          name: nameOf(empId),
          punchIn: (punch && punch.punchIn) || null,
          punchOut: (punch && punch.punchOut) || null,
          isWfh: onApprovedWfh,
          ...shiftFields,
        }
        // Approved WFH → WFH tab only (keeps punch In/Out; avoids vanishing or double-count)
        if (onApprovedWfh) {
          wfh.push(row)
        } else if (punch && punch.present) {
          present.push(row)
        } else {
          absent.push(row)
        }
      })

      const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''))
      present.sort(byName)
      absent.sort(byName)
      wfh.sort(byName)

      return {
        present,
        absent,
        wfh,
        onLeaveCount: onLeaveNonWfhIds.size,
        onLeaveEmpIds: Array.from(onLeaveNonWfhIds),
        isNonWorkingDay: false,
        nonWorkingLabel: 'Non-working day',
      }
    },

    /**
     * Regularization awaiting the current user's approval (not total reg cases).
     */
    teamRegularizationToApproveCount() {
      // Prefer client list (excludes today's local calendar day)
      const clientCount = (this.pendingRegularizationsForApproval || []).length
      if (Array.isArray(this.pendingRegularizations) || Array.isArray(this.regularizationCases)) {
        return clientCount
      }
      const apiSummary = this.teamDashboardData?.team_summary || this.teamDashboardData?.org_summary
      const apiPending =
        apiSummary &&
        (apiSummary.pending_regularization_approvals ??
          apiSummary.regularization_pending_approvals ??
          apiSummary.regularization_to_approve)
      if (apiPending != null && apiPending !== '' && Number(apiPending) > 0) {
        return Number(apiPending)
      }
      return clientCount
    },

    teamRegularizationCount() {
      const isShowingAllTeam = (this.viewMode === 'team' || this.viewMode === 'org') && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
      if (!isShowingAllTeam) return 0
      // Use dynamicTeamStats which correctly filters by selected Account Manager
      return this.dynamicTeamStats ? this.dynamicTeamStats.totalRegularization : 0
    },
    computedLeaveFields() {
      // Add employee name column when in Team View showing all team members
      const isShowingAllTeam = this.viewMode === 'team' && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
      if (isShowingAllTeam) {
        return [
          { key: 'employee_name', label: 'Employee', sortable: true },
          ...this.leaveFields
        ]
      }
      return this.leaveFields
    },
    // Computed usage of leaves by type (Approved only)
    leaveUsageByType() {
      const usage = {}
      if (!this.myLeaves || !Array.isArray(this.myLeaves)) return usage

      this.myLeaves.forEach(leave => {
        // Only count approved leaves
        if ((leave.status || '').toLowerCase() !== 'approved') return

        const leaveType = leave.type || leave.leave_type || 'Unknown'
        const leaveTypeLower = leaveType.toLowerCase()
        
        // SKIP Work From Home and CO balance requests — not counted as leaves taken
        if (typeof this.isExcludedFromLeaveKpi === 'function' ? this.isExcludedFromLeaveKpi(leave) : (leaveTypeLower.includes('work from home') || leaveTypeLower === 'wfh')) {
          return
        }
        
        // Check if it's a half-day leave
        const isHalfDay = leaveType === '1st Half Day' ||
                         leaveType === '2nd Half Day' ||
                         leaveTypeLower.includes('half day') ||
                         leave.half_day === true
        
        let mappedType = leaveType

        // Normalize types for mapping to breakdown fields
        if (leaveTypeLower.includes('casual')) {
          mappedType = 'casual_leave'
        } else if (leaveTypeLower.includes('sick') || leaveTypeLower.includes('medical')) {
          mappedType = 'sick_leave'
        } else if (leaveTypeLower.includes('privilege') || leaveTypeLower.includes('pto')) {
          mappedType = 'privilege_leave'
        } else if (isHalfDay) {
          // Half days are counted as privilege leave
          mappedType = 'privilege_leave'
        } else if (leaveTypeLower.includes('compensatory')) {
          mappedType = 'compensatory_balance'
        } else if (leaveTypeLower.includes('paternity')) {
          mappedType = 'paternity_leave'
        } else if (leaveTypeLower.includes('maternity')) {
          mappedType = 'maternity_leave'
        } else if (leaveTypeLower.includes('exam')) {
          mappedType = 'exam_leave'
        }

        // Calculate days - for half days, always use 0.5 per day in range (ignore total_days which might be wrong)
        let days = 0
        if (isHalfDay) {
          // For half-day leaves, always count as 0.5 per day (don't trust total_days from backend)
          if (leave.from_date && leave.to_date) {
            // Calculate days in range and multiply by 0.5
            const from = moment(leave.from_date)
            const to = moment(leave.to_date)
            const daysInRange = to.diff(from, 'days') + 1
            days = daysInRange * 0.5
          } else {
            days = 0.5 // Default to 0.5 for half day
          }
        } else {
          // For full day leaves, use total_days or calculate
          days = leave.total_days !== undefined && leave.total_days !== null 
            ? parseFloat(leave.total_days) 
            : 0
        }
        
        usage[mappedType] = (usage[mappedType] || 0) + days
      })
      return usage
    },
    // Computed breakdown of available leaves for display
    availableLeaveBreakdown() {
      // PRIORITY 1: If applying on behalf of an employee in team view, use team_leave_balances for that employee
      // This should take precedence over logged-in user's data
      if (this.leaveForm.selected_employee && this.viewMode === 'team' && this.teamDashboardData && this.teamDashboardData.team_leave_balances) {
        const perMember = this.teamDashboardData.team_leave_balances.per_member || []
        const memberData = perMember.find(m => m.emp_id === this.leaveForm.selected_employee)
        if (memberData && memberData.breakdown && Array.isArray(memberData.breakdown) && memberData.breakdown.length > 0) {
          // Format breakdown from team API, excluding WFM leave types
          return memberData.breakdown
            .filter(item => item.type !== 'quarterly_wfm' && item.type !== 'wfm' && item.type !== 'wfh')
            .map(item => ({
              label: item.label || item.type,
              value: item.available || 0,
              type: item.type,
              allocated: item.allocated || 0,
              assigned: item.allocated || 0, // Keep for backward compatibility
              used: item.used || 0,
              available: item.available || 0
            }))
        }
      }
      
      const selectedEmpId = this.selectedTeamMember || this.selectedTeamWiseEmployee
      const isFilteredEmployeeView = (this.viewMode === 'team' || this.viewMode === 'org') && selectedEmpId

      // Selected employee breakdown from team dashboard API (before personal API fallback)
      if (isFilteredEmployeeView && this.teamDashboardData && this.teamDashboardData.team_leave_balances) {
        const perMember = this.teamDashboardData.team_leave_balances.per_member || []
        const memberData = perMember.find(m => m.emp_id === selectedEmpId)
        if (memberData && memberData.breakdown && Array.isArray(memberData.breakdown) && memberData.breakdown.length > 0) {
          return memberData.breakdown
            .filter(item => item.type !== 'quarterly_wfm' && item.type !== 'wfm' && item.type !== 'wfh')
            .map(item => ({
              label: item.label || item.type,
              value: item.available || 0,
              type: item.type,
              allocated: item.allocated || 0,
              assigned: item.allocated || 0,
              used: item.used || 0,
              available: item.available || 0
            }))
        }
      }

      // PRIORITY 2: Use new API data for logged-in user if available
      // Skip stale personal breakdown when filtered employee view hasn't loaded that employee yet
      const personalBreakdownMatchesView = this.viewMode === 'my' ||
        !isFilteredEmployeeView ||
        String(this.currentViewEmpId) === String(selectedEmpId)
      if (personalBreakdownMatchesView && this.dashboardData && this.dashboardData.leave_balance_breakdown && this.dashboardData.leave_balance_breakdown.length > 0) {
        // Format API data to match expected structure: {label, value}, excluding WFM leave types
        return this.leaveBalanceBreakdownFromAPI
          .filter(item => item.type !== 'quarterly_wfm' && item.type !== 'wfm' && item.type !== 'wfh')
          .map(item => ({
            label: item.label || item.type,
            value: item.available || 0,
            type: item.type,
            allocated: item.allocated || 0, // New API uses 'allocated' instead of 'assigned'
            assigned: item.allocated || item.assigned || 0, // Keep 'assigned' for backward compatibility
            accrued: item.accrued || 0, // Keep for backward compatibility
            used: item.used || 0,
            available: item.available || 0,
            accrual_note: item.accrual_note
          }))
      }
      
      // Legacy fallback for backward compatibility
      // If applying on behalf of an employee, use their breakdown
      if (this.leaveForm.selected_employee && this.selectedEmployeeForLeaveConfig) {
        const stats = this.selectedEmployeeForLeaveConfig.leaveBalanceBreakdown || 
                     this.selectedEmployeeForLeaveConfig.leave_balance_breakdown || {}
        
        // Parse breakdown if it's a string
        let breakdown = stats
        if (typeof breakdown === 'string') {
          try {
            breakdown = JSON.parse(breakdown)
          } catch (e) {
            breakdown = {}
          }
        }
        
        // Calculate usage from selected employee's leaves
        const usage = this.calculateLeaveUsageForEmployee && typeof this.calculateLeaveUsageForEmployee === 'function'
          ? this.calculateLeaveUsageForEmployee(this.selectedEmployeeForLeaveLeaves)
          : {}
        
        // Ensure calculateBreakdownFromStats is available
        if (typeof this.calculateBreakdownFromStats === 'function') {
          const result = this.calculateBreakdownFromStats(breakdown, usage)
          // Filter out WFM leave types
          return result.filter(item => {
            const type = (item.type || '').toLowerCase()
            const label = (item.label || '').toLowerCase()
            return !type.includes('wfm') && !type.includes('wfh') && 
                   !label.includes('wfm') && !label.includes('wfh') &&
                   !label.includes('work from home')
          })
        } else {
          console.warn('calculateBreakdownFromStats method not available')
          return []
        }
      }
      
      // Check if we're in team view showing all team members
      const isShowingAllTeam = this.viewMode === 'team' && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
      
      // If in team view, use team_leave_balances from team API
      if (isShowingAllTeam && this.teamDashboardData && this.teamDashboardData.team_leave_balances) {
        const aggregated = this.teamDashboardData.team_leave_balances.aggregated
        if (aggregated && aggregated.by_type) {
          // Format aggregated breakdown from team API, excluding WFM leave types
          return Object.keys(aggregated.by_type)
            .filter(type => type !== 'quarterly_wfm' && type !== 'wfm' && type !== 'wfh')
            .map(type => {
              const typeData = aggregated.by_type[type]
              return {
                label: typeData.label || type,
                value: typeData.available || 0,
                type: type,
                allocated: typeData.allocated || 0,
                assigned: typeData.allocated || 0, // Keep for backward compatibility
                used: typeData.used || 0,
                available: typeData.available || 0
              }
            })
        }
      }
      
      // Legacy fallback: If in team view, aggregate breakdowns from all team members (old method)
      if (isShowingAllTeam && this.teamMemberConfigs && this.teamMemberConfigs.length > 0) {
        const result = this.calculateTeamLeaveBreakdown()
        // Filter out WFM leave types
        return result.filter(item => {
          const type = (item.type || '').toLowerCase()
          const label = (item.label || '').toLowerCase()
          return !type.includes('wfm') && !type.includes('wfh') && 
                 !label.includes('wfm') && !label.includes('wfh') &&
                 !label.includes('work from home')
        })
      }
      
      // Legacy fallback: If a specific employee is selected in team/org view, show their breakdown (old method)
      if ((this.viewMode === 'team' || this.viewMode === 'org') && selectedEmpId && this.teamMemberConfigs && this.teamMemberConfigs.length > 0) {
        const selectedEmployeeConfig = this.teamMemberConfigs.find(config => config.emp_id === selectedEmpId)
        if (selectedEmployeeConfig) {
          // The config data might be nested in a 'config' object
          const configObj = selectedEmployeeConfig.config || selectedEmployeeConfig
          
          const stats = configObj.leaveBalanceBreakdown || configObj.leave_balance_breakdown || {}
          let breakdown = stats
          if (typeof breakdown === 'string') {
            try {
              breakdown = JSON.parse(breakdown)
            } catch (e) {
              breakdown = {}
            }
          }
          
          // If breakdown is empty, try to construct from individual fields (cl, pl, sl)
          if (Object.keys(breakdown).length === 0) {
            breakdown = {
              privilege_leave: configObj.pl || configObj.privilege_leave || 0,
              casual_leave: configObj.cl || configObj.casual_leave || 0,
              sick_leave: configObj.sl || configObj.sick_leave || 0
            }
          }
          
          // Calculate usage from selected employee's leaves (from allTeamLeaves)
          const selectedEmployeeLeaves = this.allTeamLeaves.filter(leave => leave && leave.emp_id === selectedEmpId)
          const usage = this.calculateLeaveUsageForEmployee && typeof this.calculateLeaveUsageForEmployee === 'function'
            ? this.calculateLeaveUsageForEmployee(selectedEmployeeLeaves)
            : {}
          
          if (typeof this.calculateBreakdownFromStats === 'function') {
            const result = this.calculateBreakdownFromStats(breakdown, usage)
            // Filter out WFM leave types
            return result.filter(item => {
              const type = (item.type || '').toLowerCase()
              const label = (item.label || '').toLowerCase()
              return !type.includes('wfm') && !type.includes('wfh') && 
                     !label.includes('wfm') && !label.includes('wfh') &&
                     !label.includes('work from home')
            })
          }
        }
      }
      
      // Individual view: use individual stats
      const stats = (this.leaveStats && Object.keys(this.leaveStats).length > 0) 
        ? this.leaveStats 
        : (this.employeeConfig && this.employeeConfig.leaveBalanceBreakdown) || {}
      
      const usage = this.leaveUsageByType || {}
      
      // Ensure calculateBreakdownFromStats is available (methods should be accessible)
      if (typeof this.calculateBreakdownFromStats === 'function') {
        const result = this.calculateBreakdownFromStats(stats, usage)
        // Filter out WFM leave types
        return result.filter(item => {
          const type = (item.type || '').toLowerCase()
          const label = (item.label || '').toLowerCase()
          return !type.includes('wfm') && !type.includes('wfh') && 
                 !label.includes('wfm') && !label.includes('wfh') &&
                 !label.includes('work from home')
        })
      } else {
        // Fallback if method is not available yet
        console.warn('calculateBreakdownFromStats method not available')
        return []
      }
    },
    /** Breakdown with pending leave days deducted per leave type (matches KPI total). */
    displayLeaveBreakdown() {
      if (typeof this.getDisplayLeaveBreakdown !== 'function') {
        return this.availableLeaveBreakdown || []
      }
      const empId = this.selectedTeamMember || this.selectedTeamWiseEmployee || this.emp_id
      return this.getDisplayLeaveBreakdown(empId)
    },
    // HTML tooltip for Leaves Remaining — inline styles only (Bootstrap classes don't apply inside tooltips)
    leaveBreakdownHtml() {
      if (!this.displayLeaveBreakdown || !Array.isArray(this.displayLeaveBreakdown)) {
        return 'No leave balance details available'
      }
      const rows = this.displayLeaveBreakdown.filter(item => {
        if (!item) return false
        const allocated = parseFloat(item.allocated ?? item.assigned ?? item.accrued) || 0
        const used = parseFloat(item.used) || 0
        const pending = parseFloat(item.pending) || 0
        const available = parseFloat(item.value ?? item.available) || 0
        return allocated > 0 || used > 0 || pending > 0 || available > 0
      })
      if (rows.length === 0) return 'No leave balance details available'

      const totalAvailable = rows.reduce((sum, item) => sum + (parseFloat(item.value ?? item.available) || 0), 0)
      const fmt = (n) => {
        const v = parseFloat(n) || 0
        return v % 1 === 0 ? String(v) : v.toFixed(1)
      }
      const dayLabel = (n) => `${fmt(n)} day${parseFloat(n) === 1 ? '' : 's'}`
      const hasUsed = rows.some(item => (parseFloat(item.used) || 0) > 0)
      const hasPending = rows.some(item => (parseFloat(item.pending) || 0) > 0)
      const metricCols = [
        { key: 'allocated', label: 'Total allocated' },
        ...(hasUsed ? [{ key: 'used', label: 'Already used' }] : []),
        ...(hasPending ? [{ key: 'pending', label: 'Pending approval', valueStyle: 'color:#ffb74d !important;' }] : []),
        { key: 'available', label: 'Available now', valueStyle: 'color:#fff;font-weight:700;' },
      ]
      const cellValue = (item, key) => {
        const allocated = parseFloat(item.allocated ?? item.assigned ?? item.accrued) || 0
        const used = parseFloat(item.used) || 0
        const pending = parseFloat(item.pending) || 0
        const available = parseFloat(item.value ?? item.available) || 0
        if (key === 'allocated') return dayLabel(allocated)
        if (key === 'used') return used > 0 ? dayLabel(used) : ''
        if (key === 'pending') return pending > 0 ? dayLabel(pending) : ''
        if (key === 'available') return dayLabel(available)
        return ''
      }
      const thStyle = 'color:#ccc;font-weight:600;font-size:10px;line-height:1.3;padding:4px 8px 6px 0;border-bottom:1px solid rgba(255,255,255,0.35);vertical-align:bottom;white-space:normal;'
      const tdTypeStyle = 'color:#fff;font-weight:700;font-size:11px;padding:6px 10px 6px 0;border-bottom:1px solid rgba(255,255,255,0.12);vertical-align:middle;white-space:nowrap;'
      const tdMetricStyle = 'color:#fff;font-weight:600;font-size:11px;padding:6px 0 6px 8px;border-bottom:1px solid rgba(255,255,255,0.12);text-align:right;vertical-align:middle;white-space:nowrap;'

      let html = '<div class="leave-kpi-tooltip" style="color:#fff;min-width:380px;max-width:500px;text-align:left;padding:4px 2px;">'
      html += '<div style="color:#fff;font-weight:700;font-size:13px;margin-bottom:6px;">Your Leave Balance</div>'
      html += `<div style="color:#e8e8e8;font-size:11px;margin-bottom:8px;">Total available now: <span style="color:#fff;font-weight:700;">${dayLabel(totalAvailable)}</span></div>`
      html += '<table style="width:100%;border-collapse:collapse;font-size:11px;line-height:1.4;">'
      html += '<thead><tr>'
      html += `<th style="${thStyle}text-align:left;white-space:nowrap;">Leave type</th>`
      metricCols.forEach(col => {
        html += `<th style="${thStyle}text-align:right;">${col.label}</th>`
      })
      html += '</tr></thead><tbody>'
      rows.forEach(item => {
        html += '<tr>'
        html += `<td style="${tdTypeStyle}">${item.label || 'Leave'}</td>`
        metricCols.forEach(col => {
          html += `<td style="${tdMetricStyle}${col.valueStyle || ''}">${cellValue(item, col.key)}</td>`
        })
        html += '</tr>'
      })
      html += '</tbody></table>'

      if (hasPending) {
        html += '<div style="color:#ccc;font-size:10px;margin-top:8px;font-style:italic;line-height:1.4;">Pending days are reserved until approved or rejected.</div>'
      }
      html += '</div>'
      return html
    },
    displayKPIStats() {
      // PRIORITY 1: Use new API data for My View only
      // Team/org filtered views use kpiStats() derived logic or team aggregates below
      if (this.dashboardData && this.dashboardData.kpi_stats && this.viewMode === 'my') {
        return {
          leaveBalance: this.kpiStats.leave_balance ?? 0,
          leavesTaken: this.kpiStats.leaves_taken ?? 0,
          approvalPending: this.kpiStats.approval_pending ?? 0,
          regularization: this.kpiStats.regularization_count ?? 0,
        }
      }
      
      // In Team View showing all team members, show team statistics; otherwise show individual stats
      // Ensure viewMode is defined
      const viewMode = this.viewMode || 'my'
      
      // Safety check for approvalPendingCount
      const approvalPending = (typeof this.approvalPendingCount === 'number') ? this.approvalPendingCount : 0
      
      const isShowingAllTeam = (viewMode === 'team' || viewMode === 'org') && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )
      
      
      if (isShowingAllTeam) {
        // Calculate total available leaves from breakdown (sum of remaining balances)
        // This ensures the total matches the breakdown shown in the tooltip
        const breakdown = this.availableLeaveBreakdown
        const totalAvailableLeaves = breakdown.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0)
        
        // Use dynamicTeamStats for accurate team-level statistics
        const teamStats = this.dynamicTeamStats || {}
        const leavesTaken = teamStats.totalLeavesAvailed || 0
        const regularization = teamStats.totalRegularization || 0
        
        
        return {
          leaveBalance: totalAvailableLeaves, // Total available leaves (sum of breakdown)
          leavesTaken: leavesTaken, // Leaves availed
          approvalPending: approvalPending, // Pending leaves + regularizations
          regularization: regularization, // Team regularization count
        }
      }
      
      // If a specific employee is selected in team or org view, show their individual stats
      if ((viewMode === 'team' || viewMode === 'org') && (this.selectedTeamMember || this.selectedTeamWiseEmployee)) {
        return {
          leaveBalance: this.kpiStats.leave_balance ?? 0,
          leavesTaken: this.kpiStats.leaves_taken ?? 0,
          approvalPending: this.kpiStats.approval_pending ?? 0,
          regularization: this.kpiStats.regularization_count ?? 0,
        }
      }
      
      // Available Leaves = sum of display breakdown (pending already deducted per type)
      const availableLeaves = (this.displayLeaveBreakdown || []).reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0)
      
      const usageSum = Object.values(this.leaveUsageByType || {}).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
      const apiLeavesTaken = (this.leaveStats && this.leaveStats.leavesTaken) || 0
      const apiWFHCount = (this.leaveStats && this.leaveStats.WFHCount) || 0
      const derivedLeavesTaken = typeof this.getLeavesAvailedForEmployee === 'function'
        ? this.getLeavesAvailedForEmployee(this.selectedTeamMember || this.selectedTeamWiseEmployee || this.emp_id)
        : 0
      const leavesTaken = derivedLeavesTaken > 0
        ? derivedLeavesTaken
        : (usageSum > 0 ? usageSum : Math.max(0, apiLeavesTaken - apiWFHCount))
      // Safety check for getPendingRegularizationCount method
      const regularization = (typeof this.getPendingRegularizationCount === 'function') 
        ? this.getPendingRegularizationCount() 
        : 0
      return {
        leaveBalance: availableLeaves,
        leavesTaken: leavesTaken, // Leaves availed (excludes WFH & CO-REQUEST)
        approvalPending: approvalPending, // Pending leaves + regularizations
        regularization: regularization, // Only count pending regularizations (exclude approved/rejected)
      }
    },
    modalLeaveStats() {
      // Used by the leave application modal - evaluates leaveForm.selected_employee
      // only when the modal is actually open to avoid contaminating the main KPI cards
      if (this.showApplyLeaveModal && this.leaveForm.selected_employee) {
        // Try to get from team_leave_balances first (team view)
        if (this.viewMode === 'team' && this.teamDashboardData && this.teamDashboardData.team_leave_balances) {
          const perMember = this.teamDashboardData.team_leave_balances.per_member || []
          const memberData = perMember.find(m => m.emp_id === this.leaveForm.selected_employee)
          if (memberData) {
            const breakdown = this.availableLeaveBreakdown
            const totalAvailableLeaves = breakdown.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0)
            const leavesTaken = typeof this.getLeavesAvailedForEmployee === 'function'
              ? this.getLeavesAvailedForEmployee(this.leaveForm.selected_employee)
              : Math.max(0, (memberData.total_used || 0) - (memberData.wfh_count || 0))
            
            return {
              leaveBalance: totalAvailableLeaves,
              leavesTaken: leavesTaken,
              approvalPending: 0,
              regularization: 0,
            }
          }
        }
        
        // Fallback to selectedEmployeeForLeaveStats if available
        if (this.selectedEmployeeForLeaveStats) {
          const breakdown = this.availableLeaveBreakdown
          const totalAvailableLeaves = breakdown.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0)
          const leavesTaken = typeof this.getLeavesAvailedForEmployee === 'function'
            ? this.getLeavesAvailedForEmployee(this.leaveForm.selected_employee)
            : Math.max(0, (this.selectedEmployeeForLeaveStats.leavesTaken || 0) - (this.selectedEmployeeForLeaveStats.WFHCount || 0))
          
          return {
            leaveBalance: totalAvailableLeaves,
            leavesTaken: leavesTaken,
            approvalPending: 0,
            regularization: 0,
          }
        }
      }
      // Fall back to main KPI stats when no modal-specific data is needed
      return this.displayKPIStats
    },
    displayCalendarMonthYear() {
      return moment().month(this.calendarMonth).year(this.calendarYear).format('MMMM YYYY')
    },
    weekDayLabels() {
      return ['S', 'M', 'T', 'W', 'T', 'F', 'S']
    },
    leaveCalendarDays() {
      const days = []
      const startOfMonth = moment().month(this.calendarMonth).year(this.calendarYear).startOf('month')
      const endOfMonth = moment().month(this.calendarMonth).year(this.calendarYear).endOf('month')
      const startDate = startOfMonth.clone().startOf('week')
      const endDate = endOfMonth.clone().endOf('week')
      
      let currentDate = startDate.clone()
      const today = moment().startOf('day')
      const fromDate = this.leaveForm.from_date ? moment(this.leaveForm.from_date).startOf('day') : null
      const toDate = this.leaveForm.to_date ? moment(this.leaveForm.to_date).startOf('day') : null
      
      // Build disabled dates set (already applied leaves, holidays, and week offs)
      const disabledDatesSet = new Set()
      
      // Add already applied dates
      this.myLeaves.forEach(leave => {
        if (!leave.from_date || !leave.to_date) return
        const leaveFrom = moment(leave.from_date).startOf('day')
        const leaveTo = moment(leave.to_date).startOf('day')
        
        if (leave.status === 'Approved' || 
            leave.status === 'Pending Manager Approval' || 
            leave.status === 'Manager Approved - Pending HR') {
          let checkDate = leaveFrom.clone()
          while (checkDate.isSameOrBefore(leaveTo, 'day')) {
            if (checkDate.isSameOrAfter(today, 'day')) {
              disabledDatesSet.add(checkDate.format('YYYY-MM-DD'))
            }
            checkDate.add(1, 'day')
          }
        }
      })
      
      // Add holidays
      this.holidays.forEach(holiday => {
        const holidayDate = moment(holiday.holiday_date || holiday.date).startOf('day')
        if (holidayDate.isSameOrAfter(today, 'day')) {
          disabledDatesSet.add(holidayDate.format('YYYY-MM-DD'))
        }
      })
      
      // Add week offs from calendar data
      if (this.calendarDays && Array.isArray(this.calendarDays)) {
        this.calendarDays.forEach(day => {
          if (day.is_week_off || day.isWeekOff || (day.status && day.status.toLowerCase() === 'week off')) {
            const weekOffDate = moment(day.date).startOf('day')
            if (weekOffDate.isSameOrAfter(today, 'day')) {
              disabledDatesSet.add(weekOffDate.format('YYYY-MM-DD'))
            }
          }
        })
      }
      
      while (currentDate.isSameOrBefore(endDate, 'day')) {
        const dateStr = currentDate.format('YYYY-MM-DD')
        const isCurrentMonth = currentDate.month() === this.calendarMonth
        const isToday = currentDate.isSame(today, 'day')
        const isPast = currentDate.isBefore(today, 'day')
        // Sunday = 0, Saturday = 6 in moment
        const isWeekOff = currentDate.day() === 0 || currentDate.day() === 6
        
        // Check if date is disabled (past, week off, holiday, or already applied leave)
        const isAlreadyApplied = disabledDatesSet.has(dateStr)
        const isHoliday = this.holidays.some(h => {
          const holidayDate = moment(h.holiday_date || h.date).format('YYYY-MM-DD')
          return holidayDate === dateStr
        })
        // Backdating (past dates) is only allowed when this Leave form is
        // being filed on behalf of someone else — see `isApplyingLeaveOnBehalf`.
        // Week-offs, holidays and already-applied dates stay blocked regardless.
        const isDisabled = (isPast && !this.isApplyingLeaveOnBehalf) || isWeekOff || isAlreadyApplied || isHoliday
        
        // Range selection logic (for UI: show start, end, and in-between as selected range)
        let isSelectedStart = false
        let isSelectedEnd = false
        let isInRange = false
        
        if (fromDate && toDate) {
          const isSameDay = fromDate.isSame(toDate, 'day')
          if (isSameDay) {
            isSelectedStart = currentDate.isSame(fromDate, 'day')
            isSelectedEnd = currentDate.isSame(fromDate, 'day')
          } else {
            isSelectedStart = currentDate.isSame(fromDate, 'day')
            isSelectedEnd = currentDate.isSame(toDate, 'day')
            isInRange = currentDate.isAfter(fromDate, 'day') && currentDate.isBefore(toDate, 'day')
          }
        } else if (fromDate && !toDate) {
          isSelectedStart = currentDate.isSame(fromDate, 'day')
        }
        
        days.push({
          date: currentDate.date(),
          dateStr,
          isCurrentMonth,
          isToday,
          isPast,
          isWeekOff,
          isSelectedStart,
          isSelectedEnd,
          isInRange,
          isDisabled,
          isAlreadyApplied,
          isHoliday,
        })
        
        currentDate.add(1, 'day')
      }
      
      return days
    },
    displayWFHCalendarMonthYear() {
      return moment().month(this.wfhCalendarMonth).year(this.wfhCalendarYear).format('MMMM YYYY')
    },
    wfhCalendarDays() {
      const days = []
      const startOfMonth = moment().month(this.wfhCalendarMonth).year(this.wfhCalendarYear).startOf('month')
      const endOfMonth = moment().month(this.wfhCalendarMonth).year(this.wfhCalendarYear).endOf('month')
      const startDate = startOfMonth.clone().startOf('week')
      const endDate = endOfMonth.clone().endOf('week')
      
      let currentDate = startDate.clone()
      const today = moment().startOf('day')
      const selectedDate = this.wfhForm.date ? moment(this.wfhForm.date).startOf('day') : null
      
      // Build disabled dates set (already applied leaves and holidays)
      const disabledDatesSet = new Set()
      
      // Add already applied dates (including WFH)
      this.myLeaves.forEach(leave => {
        if (!leave.from_date || !leave.to_date) return
        const leaveFrom = moment(leave.from_date).startOf('day')
        const leaveTo = moment(leave.to_date).startOf('day')
        
        if (leave.status === 'Approved' || 
            leave.status === 'Pending Manager Approval' || 
            leave.status === 'Manager Approved - Pending HR') {
          let checkDate = leaveFrom.clone()
          while (checkDate.isSameOrBefore(leaveTo, 'day')) {
            if (checkDate.isSameOrAfter(today, 'day')) {
              disabledDatesSet.add(checkDate.format('YYYY-MM-DD'))
            }
            checkDate.add(1, 'day')
          }
        }
      })
      
      // Add holidays
      this.holidays.forEach(holiday => {
        const holidayDate = moment(holiday.holiday_date || holiday.date).startOf('day')
        if (holidayDate.isSameOrAfter(today, 'day')) {
          disabledDatesSet.add(holidayDate.format('YYYY-MM-DD'))
        }
      })
      
      while (currentDate.isSameOrBefore(endDate, 'day')) {
        const dateStr = currentDate.format('YYYY-MM-DD')
        const isCurrentMonth = currentDate.month() === this.wfhCalendarMonth
        const isToday = currentDate.isSame(today, 'day')
        const isPast = currentDate.isBefore(today, 'day')
        
        // Check if date is disabled
        const isAlreadyApplied = disabledDatesSet.has(dateStr)
        const isHoliday = this.holidays.some(h => {
          const holidayDate = moment(h.holiday_date || h.date).format('YYYY-MM-DD')
          return holidayDate === dateStr
        })
        // Company-Wide WFH already covers the day — no personal WFH application
        const isCompanyWfh = !!(this.isCompanyWfhDate && this.isCompanyWfhDate(dateStr))
        // Backdating (past dates) is only allowed when this WFH form is
        // being filed on behalf of someone else — see `isApplyingWfhOnBehalf`.
        const isDisabled = (isPast && !this.isApplyingWfhOnBehalf) || isAlreadyApplied || isHoliday || isCompanyWfh
        
        // Selection logic (single date for WFH)
        const isSelected = selectedDate && currentDate.isSame(selectedDate, 'day')
        
        days.push({
          date: currentDate.date(),
          dateStr,
          isCurrentMonth,
          isToday,
          isPast,
          isSelected,
          isDisabled,
          isAlreadyApplied,
          isHoliday,
        })
        
        currentDate.add(1, 'day')
      }
      
      return days
    },
    calendarWeeks() {
      // Use new API data if available (pre-built calendar)
      if (this.dashboardData && this.dashboardData.calendar_data && this.dashboardData.calendar_data.length > 0) {
        const apiDays = this.calendarDays
        const weeks = []
        
        // Create a map of API days by date for quick lookup
        const apiDaysMap = {}
        apiDays.forEach(apiDay => {
          apiDaysMap[apiDay.date] = apiDay
        })
        
        // Build full calendar grid (including previous/next month padding)
        const startOfMonth = moment().month(this.selectedMonth).year(this.selectedYear).startOf('month')
        const endOfMonth = moment().month(this.selectedMonth).year(this.selectedYear).endOf('month')
        const startDate = startOfMonth.clone().startOf('week')
        const endDate = endOfMonth.clone().endOf('week')
        
        let currentDate = startDate.clone()
        const today = moment()
        
        while (currentDate.isSameOrBefore(endDate, 'day')) {
          const week = []
          for (let i = 0; i < 7; i++) {
            const dateStr = currentDate.format('YYYY-MM-DD')
            const apiDay = apiDaysMap[dateStr]
            
            if (apiDay) {
              // Use API data for this day
              // Determine leave status from API data
              const isLeave = apiDay.leave_type && 
                             apiDay.leave_type !== 'Present' && 
                             apiDay.leave_type !== 'Absent' &&
                             apiDay.status !== 'present' &&
                             apiDay.status !== 'absent'
              
              const isMaternityPaternity = apiDay.leave_type && 
                                           (apiDay.leave_type === 'Maternity' || apiDay.leave_type === 'Paternity')
              
              const isWFH = apiDay.leave_type && 
                           (apiDay.leave_type.toLowerCase().includes('work from home') || 
                            apiDay.leave_type.toLowerCase().includes('wfh'))
              
              const isOtherLeave = isLeave && !isMaternityPaternity && !isWFH
              
              // Determine present/absent from status
              const isPresent = apiDay.status === 'present' || apiDay.leave_type === 'Present'
              const isAbsent = apiDay.status === 'absent' || apiDay.leave_type === 'Absent'
              // Overlay client-side Company-Wide WFH list onto API calendar.
              // BACKEND GAP: `is_company_wfh` may be missing — until it ships,
              // isCompanyWfhDate() covers declared days from getCompanyWfhDays().
              // Co. WFH is a calendar marker only — punch is still required, so
              // keep Absent / Regularization as the API reports them.
              const isCompanyWfh = !!(apiDay.is_company_wfh || (this.isCompanyWfhDate && this.isCompanyWfhDate(dateStr)))
              
              week.push({
                date: dateStr,
                day: apiDay.day,
                isCurrentMonth: apiDay.is_current_month || false,
                isToday: apiDay.is_today || false,
                isHoliday: apiDay.is_holiday || false,
                isCompanyWfh,
                isWeekOff: apiDay.is_weekend || false,
                isLeave: isLeave || false,
                isMaternityPaternityLeave: isMaternityPaternity || false,
                isOtherLeave: isOtherLeave || false,
                isWFH: isWFH || false,
                leaveType: apiDay.leave_type || null,
                isPresent: isPresent || false,
                isAbsent: isAbsent || false,
                isRegularization: apiDay.is_regularization || false,
                hasBiometric: apiDay.has_biometric || false,
                biometricRecord: null, // API doesn't provide full biometric record
                attendanceRecord: null, // API doesn't provide full attendance record
                workHours: apiDay.work_hours || null,
                regularizationIssue: apiDay.is_regularization ? (apiDay.regularization_status || 'pending') : null,
                inTime: apiDay.in_time || null,
                outTime: apiDay.out_time || null,
                remarks: apiDay.remarks || null,
                hasPunchData: apiDay.has_punch_data || false,
                sourceType: apiDay.source_type || null,
              })
            } else {
              // No API data for this day (previous/next month padding) - use minimal structure
              const isCompanyWfh = !!(this.isCompanyWfhDate && this.isCompanyWfhDate(dateStr))
              week.push({
                date: dateStr,
                day: currentDate.date(),
                isCurrentMonth: currentDate.month() === this.selectedMonth,
                isToday: currentDate.isSame(today, 'day'),
                isHoliday: false,
                isCompanyWfh,
                isWeekOff: currentDate.day() === 0 || currentDate.day() === 6,
                isLeave: false,
                isMaternityPaternityLeave: false,
                isOtherLeave: false,
                isWFH: false,
                leaveType: null,
                isPresent: false,
                isAbsent: false,
                isRegularization: false,
                hasBiometric: false,
                biometricRecord: null,
                attendanceRecord: null,
                workHours: null,
                regularizationIssue: null,
                inTime: null,
                outTime: null,
                remarks: null,
                hasPunchData: false,
                sourceType: null,
              })
            }
            currentDate.add(1, 'day')
          }
          weeks.push(week)
        }
        return weeks
      }
      
      // Legacy fallback - build calendar from raw data
      const weeks = []
      const startOfMonth = moment().month(this.selectedMonth).year(this.selectedYear).startOf('month')
      const endOfMonth = moment().month(this.selectedMonth).year(this.selectedYear).endOf('month')
      const startDate = startOfMonth.clone().startOf('week')
      const endDate = endOfMonth.clone().endOf('week')
      
      let currentDate = startDate.clone()
      const today = moment()
      
      while (currentDate.isSameOrBefore(endDate, 'day')) {
        const week = []
        for (let i = 0; i < 7; i++) {
          const dateStr = currentDate.format('YYYY-MM-DD')
          const isHoliday = this.isHolidayDate(dateStr)
          // Same date-membership-match style as isHolidayDate() above —
          // see AttendanceDashboard.vue#isCompanyWfhDate.
          const isCompanyWfh = this.isCompanyWfhDate(dateStr)
          const isWeekOff = currentDate.day() === 0 || currentDate.day() === 6
          
          // Get biometric data
          const biometric = this.biometricData.find(b => b.datestring === dateStr)
          const attendance = this.attendanceData.find(a => a.date === dateStr)
          
          // Get leave data - use API data if available
          const leavesToSearch = (this.myLeavesFromAPI && this.myLeavesFromAPI.length > 0) ? this.myLeavesFromAPI : this.myLeaves
          const leave = leavesToSearch.find(l => {
            const from = moment(l.from_date)
            const to = moment(l.to_date)
            return currentDate.isSameOrAfter(from, 'day') && currentDate.isSameOrBefore(to, 'day')
          })
          
          // Get work hours from attendance API (punch-data) only
          let workHours = null
          if (attendance && attendance.worktime) {
            workHours = this.parseWorkTime(attendance.worktime)
          }
          
          // Check if this date needs regularization - use API data if available
          const regCasesToSearch = (this.regularizationCasesFromAPI && this.regularizationCasesFromAPI.length > 0) ? this.regularizationCasesFromAPI : this.regularizationCases
          const needsRegularization = regCasesToSearch.some(reg => reg.datestring === dateStr)
          
          const isMaternityPaternity = leave && (leave.leave_type === 'Maternity' || leave.leave_type === 'Paternity')
          const isOtherLeave = leave && !this.isWFH(leave.leave_type) && leave.status === 'Approved' && !isMaternityPaternity
          // Company-Wide WFH still requires punch — surface Absent / Reg like a normal work day.
          const dayNeedsReg = needsRegularization
          
          const day = {
            date: dateStr,
            day: currentDate.date(),
            isCurrentMonth: currentDate.month() === this.selectedMonth,
            isToday: currentDate.isSame(today, 'day'),
            isHoliday,
            isCompanyWfh,
            isWeekOff,
            isLeave: leave && !this.isWFH(leave.leave_type) && leave.status === 'Approved',
            isMaternityPaternityLeave: isMaternityPaternity && leave.status === 'Approved',
            isOtherLeave: isOtherLeave,
            isWFH: leave && this.isWFH(leave.leave_type) && leave.status === 'Approved',
            leaveType: leave ? leave.leave_type : null,
            isPresent: biometric && this.getBiometricStatus(biometric) === 'present',
            isAbsent: biometric && this.getBiometricStatus(biometric) === 'absent',
            isRegularization: dayNeedsReg,
            hasBiometric: !!biometric,
            biometricRecord: biometric,
            attendanceRecord: attendance,
            workHours: workHours ? workHours.toFixed(1) : null,
            regularizationIssue: dayNeedsReg ? regCasesToSearch.find(reg => reg.datestring === dateStr)?.issue_type : null,
          }
          week.push(day)
          currentDate.add(1, 'day')
        }
        weeks.push(week)
      }
      
      return weeks
    },
    upcomingHolidays() {
      const today = moment()
      const holidaysToUse = this.holidaysFromAPI.length > 0 ? this.holidaysFromAPI : this.holidays
      return holidaysToUse
        .filter(h => {
          const holidayDate = moment(h.holiday_date || h.date)
          return holidayDate.isAfter(today, 'day')
        })
        .sort((a, b) => {
          const dateA = moment(a.holiday_date || a.date)
          const dateB = moment(b.holiday_date || b.date)
          return dateA.diff(dateB)
        })
    },
    // Mirrors upcomingHolidays() exactly — no "FromAPI" merge variant yet
    // since the consolidated dashboard endpoint doesn't return this list
    // (backend gap, see backend-handoff/PUNCH_ATTENDANCE_API_DOCUMENTATION.md); reads from
    // the dedicated `companyWfhDays` list loaded via loadCompanyWfhDays().
    upcomingCompanyWfhDays() {
      const today = moment()
      return (this.companyWfhDays || [])
        .filter(d => moment(d.date).isAfter(today, 'day'))
        .sort((a, b) => moment(a.date).diff(moment(b.date)))
    },
    upcomingLeaves() {
      // Use API data if available, fallback to legacy
      const myLeavesToUse = (this.myLeavesFromAPI && this.myLeavesFromAPI.length > 0) ? this.myLeavesFromAPI : this.myLeaves
      const isTeamOrOrg = this.viewMode === 'team' || this.viewMode === 'org'
      const filterEmpId = this.selectedTeamMember || this.selectedTeamWiseEmployee
      const isShowingAllTeam = isTeamOrOrg && (
        (this.canApplyOnBehalf && !this.canApplyOnBehalfAll && !this.selectedTeamMember) ||
        (this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
        (this.canApplyOnBehalfAll && !this.selectedAccountManager)
      )

      let leavesToFilter
      if (isTeamOrOrg && filterEmpId) {
        // Single employee selected via main filter — use that employee's leaves only
        leavesToFilter = (myLeavesToUse || []).filter(
          leave => leave && String(leave.emp_id) === String(filterEmpId)
        )
      } else if (isTeamOrOrg) {
        leavesToFilter = Array.isArray(this.allTeamLeaves) ? this.allTeamLeaves.flat() : []
        const amTeamIds = this.accountManagerTeamMemberIds
        if (amTeamIds && amTeamIds.length > 0) {
          leavesToFilter = leavesToFilter.filter(leave => leave && amTeamIds.includes(leave.emp_id))
        }
      } else {
        leavesToFilter = myLeavesToUse || []
      }
        
      if (!leavesToFilter || leavesToFilter.length === 0) {
        return []
      }
      
      const today = moment().startOf('day')
      
      const filtered = leavesToFilter
        .filter(leave => {
          if (!leave || !leave.from_date) {
            return false
          }
          
          try {
            const fromDateStr = String(leave.from_date).trim()
            let fromDate = moment(fromDateStr, 'YYYY-MM-DD', true)
            if (!fromDate.isValid()) {
              fromDate = moment(fromDateStr)
            }
            if (!fromDate.isValid()) {
              return false
            }

            const fromDateStart = fromDate.startOf('day')
            const status = (leave.status || '').toLowerCase()

            if (isShowingAllTeam) {
              // Team/org aggregate view — match upcomingTeamLeaves (KPI card) logic
              const isApproved = status === 'approved' || status === 'manager approved - pending hr'
              const isUpcoming = fromDateStart.isSameOrAfter(today, 'day')
              return isApproved && isUpcoming
            }

            // My view / single employee — include pending and future-only leaves
            const isFuture = fromDateStart.isAfter(today, 'day')
            const statusRaw = String(leave.status || '').trim()
            const hasValidStatus =
              statusRaw === 'Approved' ||
              statusRaw === 'Pending Manager Approval' ||
              statusRaw === 'Manager Approved - Pending HR' ||
              status.toLowerCase().includes('approved') ||
              status.toLowerCase().includes('pending')

            return isFuture && hasValidStatus
          } catch (e) {
            return false
          }
        })
        .sort((a, b) => {
          try {
            const dateA = moment(a.from_date, 'YYYY-MM-DD', true).startOf('day')
            const dateB = moment(b.from_date, 'YYYY-MM-DD', true).startOf('day')
            if (!dateA.isValid() || !dateB.isValid()) return 0
            return dateA.diff(dateB)
          } catch (e) {
            return 0
          }
        })
        .slice(0, 10)
      
      return filtered
    },
    /**
     * Approved upcoming leaves starting within the next 7 days (manager compact list).
     */
    upcomingLeavesNext7Days() {
      const today = moment().startOf('day')
      const end = moment().add(7, 'days').endOf('day')
      const source = (this.upcomingTeamLeaves && this.upcomingTeamLeaves.length)
        ? this.upcomingTeamLeaves
        : (this.upcomingLeaves || [])
      return (source || []).filter(leave => {
        if (!leave || !leave.from_date) return false
        try {
          const from = moment(leave.from_date).startOf('day')
          if (!from.isValid()) return false
          return from.isSameOrAfter(today, 'day') && from.isSameOrBefore(end, 'day')
        } catch (e) {
          return false
        }
      }).slice(0, 10)
    },

    /**
     * Birthdays falling within the current calendar week (Mon–Sun), if DOB data exists.
     */
    birthdaysThisWeek() {
      const rawBirthdays = this.dashboardData?.birthdays ?? this.birthdays
      const birthdaysData = Array.isArray(rawBirthdays) ? rawBirthdays : []
      if (!birthdaysData.length) return []

      const today = moment().startOf('day')
      const weekStart = today.clone().startOf('isoWeek')
      const weekEnd = today.clone().endOf('isoWeek')

      return birthdaysData.map(b => {
        const birthdayDate = moment(b.date || b.birthday_date || b.dob || b.date_of_birth)
        if (!birthdayDate.isValid()) return null
        const thisYear = moment().year(moment().year()).month(birthdayDate.month()).date(birthdayDate.date()).startOf('day')
        const displayDate = thisYear.isBefore(weekStart)
          ? thisYear.clone().add(1, 'year')
          : thisYear
        if (!displayDate.isBetween(weekStart, weekEnd, 'day', '[]')) return null
        const name = b.name
          || [b.first_name, b.last_name].filter(Boolean).join(' ')
          || (b.emp_id && typeof this.getUserName === 'function' ? this.getUserName(b.emp_id) : null)
          || b.emp_id
          || 'Employee'
        return {
          id: b.id || `birthday-${b.emp_id || name}`,
          emp_id: b.emp_id,
          name,
          date: displayDate.format('YYYY-MM-DD'),
          displayDateLabel: displayDate.format('ddd D MMM'),
        }
      }).filter(Boolean).sort((a, b) => moment(a.date).diff(moment(b.date)))
    },

    /** Active announcement-only count for snapshot teaser (excludes birthdays/anniversaries). */
    activeAnnouncementTeaserCount() {
      const today = moment().startOf('day')
      const endLimit = moment().add(20, 'days').endOf('day')
      return (this.announcements || []).filter(a => {
        const isActive = (a.status || 'ACTIVE') === 'ACTIVE'
        if (!isActive) return false
        const date = moment(a.date)
        return date.isValid() && date.isBetween(today, endLimit, 'day', '[]')
      }).length
    },

    frequentRegularizationCases() {
      // Employees with > 3 regularizations in last 30 days
      if ((this.viewMode !== 'team' && this.viewMode !== 'org')
        || !this.regularizationCases
        || !Array.isArray(this.regularizationCases)) {
        return []
      }
      
      const thirtyDaysAgo = moment().subtract(30, 'days').startOf('day')
      const regularizationCounts = {}
      
      this.regularizationCases.forEach(reg => {
        const empId = reg.emp_id
        if (!empId) return
        
        try {
          const regDate = moment(reg.date || reg.datestring || reg.attendance_date)
          if (regDate.isValid() && regDate.isAfter(thirtyDaysAgo)) {
            if (!regularizationCounts[empId]) {
              regularizationCounts[empId] = { emp_id: empId, count: 0 }
            }
            regularizationCounts[empId].count++
          }
        } catch (e) {
          // Skip invalid dates
        }
      })
      
      return Object.values(regularizationCounts)
        .filter(item => item.count > 3)
        .sort((a, b) => b.count - a.count)
    },
    upcomingLeaveConflicts() {
      // Dates where multiple employees are on leave
      if (this.viewMode !== 'team' && this.viewMode !== 'org') {
        return []
      }
      // Single employee selected — no cross-employee conflicts to show
      if (this.selectedTeamMember || this.selectedTeamWiseEmployee) {
        return []
      }
      if (!this.allTeamLeaves || this.allTeamLeaves.length === 0) {
        return []
      }
      
      const today = moment().startOf('day')
      const dateMap = {}
      
      const teamLeaves = Array.isArray(this.allTeamLeaves) ? this.allTeamLeaves.flat() : []
      teamLeaves.forEach(leave => {
        if (!leave.from_date || !leave.to_date) return
        
        const status = (leave.status || '').toLowerCase()
        // Only consider approved or pending leaves
        if (!status.includes('approved') && !status.includes('pending')) return
        
        try {
          const fromDate = moment(leave.from_date).startOf('day')
          const toDate = moment(leave.to_date).startOf('day')
          
          if (!fromDate.isValid() || !toDate.isValid()) return
          if (toDate.isBefore(today)) return // Skip past leaves
          
          // Iterate through each day of the leave
          let currentDate = fromDate.clone()
          while (currentDate.isSameOrBefore(toDate) && currentDate.isSameOrAfter(today)) {
            const dateStr = currentDate.format('YYYY-MM-DD')
            
            if (!dateMap[dateStr]) {
              dateMap[dateStr] = {
                date: dateStr,
                employees: []
              }
            }
            
            // Add employee if not already in the list
            const empId = leave.emp_id
            if (empId && !dateMap[dateStr].employees.find(e => e.emp_id === empId)) {
              dateMap[dateStr].employees.push({
                emp_id: empId,
                leave_type: leave.leave_type
              })
            }
            
            currentDate.add(1, 'day')
          }
        } catch (e) {
          // Skip invalid dates
        }
      })
      
      // Filter to only dates with 2+ employees
      return Object.values(dateMap)
        .filter(item => item.employees.length >= 2)
        .sort((a, b) => moment(a.date).diff(moment(b.date)))
    },
    employeesWithLowLeaveBalance() {
      // Employees with leave balance < 5 days
      if ((this.viewMode !== 'team' && this.viewMode !== 'org')
        || !this.teamMembers
        || this.teamMembers.length === 0) {
        return []
      }
      
      // If a specific employee is selected, filter to only that employee
      const filterByEmpId = this.selectedTeamMember || this.selectedTeamWiseEmployee
      
      
      const lowBalanceEmployees = []
      const threshold = 5
      
      // Get leave balances from teamMemberLeaveBalances
      this.teamMemberLeaveBalances.forEach(balance => {
        // If filtering by specific employee, skip others
        if (filterByEmpId && balance.emp_id !== filterByEmpId) return
        const leaveBalance = balance.leaveBalance || 0
        if (leaveBalance < threshold && leaveBalance >= 0) {
          const leavesTaken = typeof this.getLeavesAvailedForEmployee === 'function'
            ? this.getLeavesAvailedForEmployee(balance.emp_id)
            : Math.max(0, (balance.leavesTaken || 0) - (balance.WFHCount || 0))
          lowBalanceEmployees.push({
            emp_id: balance.emp_id,
            leaveBalance: leaveBalance,
            leavesTaken: leavesTaken
          })
        }
      })
      
      return lowBalanceEmployees.sort((a, b) => a.leaveBalance - b.leaveBalance)
    },
  },
}
